import * as i0 from '@angular/core';
import { EventEmitter, Component, Input, Output, Directive, SkipSelf, Optional, forwardRef, Host, ContentChild, HostListener, ContentChildren, ViewChild, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Map, Feature, Graticule } from 'ol';
import { register } from 'ol/proj/proj4';
import proj4 from 'proj4';
import { Vector as Vector$1, XYZ, OSM, BingMaps, Cluster, WMTS as WMTS$1, VectorTile as VectorTile$1, TileWMS, TileJSON, ImageStatic, ImageWMS, Raster, UTFGrid } from 'ol/source';
import { Group, Vector, Image, Tile, VectorTile } from 'ol/layer';
import View from 'ol/View';
import { createXYZ } from 'ol/tilegrid';
import TileGrid from 'ol/tilegrid/TileGrid';
import WMTS from 'ol/tilegrid/WMTS';
import { GeoJSON, MVT } from 'ol/format';
import ImageArcGISRest from 'ol/source/ImageArcGISRest';
import { Circle, LineString, MultiLineString, MultiPoint, MultiPolygon, Point, Polygon, LinearRing } from 'ol/geom';
import { transform } from 'ol/proj';
import Overlay from 'ol/Overlay';
import { Style, Circle as Circle$1, Text, Stroke, Icon, Fill } from 'ol/style';
import { defaults, Control, Attribution, FullScreen, OverviewMap, Rotate, ScaleLine, Zoom, ZoomSlider, ZoomToExtent } from 'ol/control';
import MousePosition from 'ol/control/MousePosition';
import { defaults as defaults$1, DoubleClickZoom, DragAndDrop, DragBox, DragPan, DragRotate, DragRotateAndZoom, DragZoom, MouseWheelZoom, PinchZoom, Draw, KeyboardPan, KeyboardZoom, Select, Modify, Translate } from 'ol/interaction';
import { platformModifierKey } from 'ol/events/condition';
import { containsCoordinate } from 'ol/extent';
import VectorLayer from 'ol/layer/Vector';

var ProjectionCode;
(function (ProjectionCode) {
    ProjectionCode["PseudoMercator"] = "EPSG:3857";
    ProjectionCode["Pl2000zone5"] = "EPSG:2176";
    ProjectionCode["Pl2000zone6"] = "EPSG:2177";
    ProjectionCode["Pl2000zone7"] = "EPSG:2178";
    ProjectionCode["Pl2000zone8"] = "EPSG:2179";
})(ProjectionCode || (ProjectionCode = {}));
var ProjectionCodeDefinition;
(function (ProjectionCodeDefinition) {
    ProjectionCodeDefinition["Pl2000zone5"] = "+proj=tmerc +lat_0=0 +lon_0=15 +k=0.999923 +x_0=5500000 +y_0=0 +ellps=GRS80 +units=m +no_defs";
    ProjectionCodeDefinition["Pl2000zone6"] = "+proj=tmerc +lat_0=0 +lon_0=18 +k=0.999923 +x_0=6500000 +y_0=0 +ellps=GRS80 +units=m +no_defs";
    ProjectionCodeDefinition["Pl2000zone7"] = "+proj=tmerc +lat_0=0 +lon_0=21 +k=0.999923 +x_0=7500000 +y_0=0 +ellps=GRS80 +units=m +no_defs";
    ProjectionCodeDefinition["Pl2000zone8"] = "+proj=tmerc +lat_0=0 +lon_0=24 +k=0.999923 +x_0=8500000 +y_0=0 +ellps=GRS80 +units=m +no_defs";
})(ProjectionCodeDefinition || (ProjectionCodeDefinition = {}));

class MapComponent {
    constructor(host) {
        this.host = host;
        this.width = '100%';
        this.height = '100%';
        this.olChange = new EventEmitter();
        this.olChangeLayerGroup = new EventEmitter();
        this.olChangeSize = new EventEmitter();
        this.olChangeTarget = new EventEmitter();
        this.olChangeView = new EventEmitter();
        this.olClick = new EventEmitter();
        this.dblClick = new EventEmitter();
        this.olError = new EventEmitter();
        this.loadEnd = new EventEmitter();
        this.loadStart = new EventEmitter();
        this.moveEnd = new EventEmitter();
        this.moveStart = new EventEmitter();
        this.pointerDrag = new EventEmitter();
        this.pointerMove = new EventEmitter();
        this.olPostCompose = new EventEmitter();
        this.olPostRender = new EventEmitter();
        this.olPreCompose = new EventEmitter();
        this.olPropertyChange = new EventEmitter();
        this.postRender = new EventEmitter();
        this.propertyChange = new EventEmitter();
        this.singleClick = new EventEmitter();
        this.componentType = 'map';
        // we pass empty arrays to not get default controls/interactions because we have our own directives
        this.controls = [];
        this.interactions = [];
    }
    ngOnInit() {
        // console.log('creating ol.Map instance with:', this);
        this.setProjectionDefinitions();
        this.instance = new Map(this);
        this.instance.setTarget(this.host.nativeElement.firstElementChild);
        this.instance.on('change', (event) => this.olChange.emit(event));
        this.instance.on('change:layergroup', (event) => this.olChangeLayerGroup.emit(event));
        this.instance.on('change:size', (event) => this.olChangeSize.emit(event));
        this.instance.on('change:target', (event) => this.olChangeTarget.emit(event));
        this.instance.on('change:view', (event) => this.olChangeView.emit(event));
        this.instance.on('click', (event) => this.olClick.emit(event));
        this.instance.on('dblclick', (event) => this.dblClick.emit(event));
        this.instance.on('error', (event) => this.olError.emit(event));
        this.instance.on('loadend', (event) => this.loadEnd.emit(event));
        this.instance.on('loadstart', (event) => this.loadStart.emit(event));
        this.instance.on('moveend', (event) => this.moveEnd.emit(event));
        this.instance.on('movestart', (event) => this.moveStart.emit(event));
        this.instance.on('pointerdrag', (event) => this.pointerDrag.emit(event));
        this.instance.on('pointermove', (event) => this.pointerMove.emit(event));
        this.instance.on('postcompose', (event) => this.olPostCompose.emit(event));
        this.instance.on('postrender', (event) => this.olPostRender.emit(event));
        this.instance.on('postrender', (event) => this.postRender.emit(event));
        this.instance.on('precompose', (event) => this.olPreCompose.emit(event));
        this.instance.on('propertychange', (event) => this.olPropertyChange.emit(event));
        this.instance.on('singleclick', (event) => this.singleClick.emit(event));
    }
    ngOnChanges(changes) {
        const properties = {};
        if (!this.instance) {
            return;
        }
        for (const key in changes) {
            if (changes.hasOwnProperty(key)) {
                properties[key] = changes[key].currentValue;
            }
        }
        // console.log('changes detected in aol-map, setting new properties: ', properties);
        this.instance.setProperties(properties, false);
    }
    ngAfterViewInit() {
        this.instance.updateSize();
    }
    setProjectionDefinitions() {
        proj4.defs(ProjectionCode.Pl2000zone5, ProjectionCodeDefinition.Pl2000zone5);
        proj4.defs(ProjectionCode.Pl2000zone6, ProjectionCodeDefinition.Pl2000zone6);
        proj4.defs(ProjectionCode.Pl2000zone7, ProjectionCodeDefinition.Pl2000zone7);
        proj4.defs(ProjectionCode.Pl2000zone8, ProjectionCodeDefinition.Pl2000zone8);
        register(proj4);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: MapComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: MapComponent, isStandalone: true, selector: "aol-map", inputs: { width: "width", height: "height", pixelRatio: "pixelRatio", keyboardEventTarget: "keyboardEventTarget", loadTilesWhileAnimating: "loadTilesWhileAnimating", loadTilesWhileInteracting: "loadTilesWhileInteracting", logo: "logo", renderer: "renderer" }, outputs: { olChange: "olChange", olChangeLayerGroup: "olChangeLayerGroup", olChangeSize: "olChangeSize", olChangeTarget: "olChangeTarget", olChangeView: "olChangeView", olClick: "olClick", dblClick: "dblClick", olError: "olError", loadEnd: "loadEnd", loadStart: "loadStart", moveEnd: "moveEnd", moveStart: "moveStart", pointerDrag: "pointerDrag", pointerMove: "pointerMove", olPostCompose: "olPostCompose", olPostRender: "olPostRender", olPreCompose: "olPreCompose", olPropertyChange: "olPropertyChange", postRender: "postRender", propertyChange: "propertyChange", singleClick: "singleClick" }, usesOnChanges: true, ngImport: i0, template: `
    <div [style.width]="width" [style.height]="height"></div>
    <ng-content></ng-content>
  `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: MapComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-map',
                    template: `
    <div [style.width]="width" [style.height]="height"></div>
    <ng-content></ng-content>
  `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { width: [{
                type: Input
            }], height: [{
                type: Input
            }], pixelRatio: [{
                type: Input
            }], keyboardEventTarget: [{
                type: Input
            }], loadTilesWhileAnimating: [{
                type: Input
            }], loadTilesWhileInteracting: [{
                type: Input
            }], logo: [{
                type: Input
            }], renderer: [{
                type: Input
            }], olChange: [{
                type: Output
            }], olChangeLayerGroup: [{
                type: Output
            }], olChangeSize: [{
                type: Output
            }], olChangeTarget: [{
                type: Output
            }], olChangeView: [{
                type: Output
            }], olClick: [{
                type: Output
            }], dblClick: [{
                type: Output
            }], olError: [{
                type: Output
            }], loadEnd: [{
                type: Output
            }], loadStart: [{
                type: Output
            }], moveEnd: [{
                type: Output
            }], moveStart: [{
                type: Output
            }], pointerDrag: [{
                type: Output
            }], pointerMove: [{
                type: Output
            }], olPostCompose: [{
                type: Output
            }], olPostRender: [{
                type: Output
            }], olPreCompose: [{
                type: Output
            }], olPropertyChange: [{
                type: Output
            }], postRender: [{
                type: Output
            }], propertyChange: [{
                type: Output
            }], singleClick: [{
                type: Output
            }] } });

// eslint-disable-next-line @angular-eslint/directive-class-suffix
class LayerComponent {
    constructor(host) {
        this.host = host;
        this.componentType = 'layer';
    }
    ngOnInit() {
        if (this.prerender !== null && this.prerender !== undefined) {
            this.instance.on('prerender', this.prerender);
        }
        if (this.postrender !== null && this.postrender !== undefined) {
            this.instance.on('postrender', this.postrender);
        }
        this.host.instance.getLayers().push(this.instance);
    }
    ngOnDestroy() {
        this.host.instance.getLayers().remove(this.instance);
    }
    ngOnChanges(changes) {
        const properties = {};
        if (!this.instance) {
            return;
        }
        for (const key in changes) {
            if (changes.hasOwnProperty(key)) {
                properties[key] = changes[key].currentValue;
                if (key === 'prerender') {
                    this.instance.un('prerender', changes[key].previousValue);
                    this.instance.on('prerender', changes[key].currentValue);
                }
                if (key === 'postrender') {
                    this.instance.un('postrender', changes[key].previousValue);
                    this.instance.on('postrender', changes[key].currentValue);
                }
            }
        }
        // console.log('changes detected in aol-layer, setting new properties: ', properties);
        this.instance.setProperties(properties, false);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: LayerComponent, deps: "invalid", target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.3", type: LayerComponent, isStandalone: true, inputs: { opacity: "opacity", visible: "visible", extent: "extent", zIndex: "zIndex", minResolution: "minResolution", maxResolution: "maxResolution", prerender: "prerender", postrender: "postrender" }, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: LayerComponent, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: undefined }], propDecorators: { opacity: [{
                type: Input
            }], visible: [{
                type: Input
            }], extent: [{
                type: Input
            }], zIndex: [{
                type: Input
            }], minResolution: [{
                type: Input
            }], maxResolution: [{
                type: Input
            }], prerender: [{
                type: Input
            }], postrender: [{
                type: Input
            }] } });

// eslint-disable-next-line @angular-eslint/directive-class-suffix
class SourceComponent {
    constructor(host) {
        this.host = host;
        this.componentType = 'source';
    }
    ngOnDestroy() {
        if (this.host && this.host.instance) {
            this.host.instance.setSource(null);
        }
    }
    register(s) {
        if (this.host) {
            this.host.instance.setSource(s);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceComponent, deps: [{ token: LayerComponent }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.3", type: SourceComponent, isStandalone: true, inputs: { attributions: "attributions" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceComponent, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: LayerComponent }], propDecorators: { attributions: [{
                type: Input
            }] } });

class LayerGroupComponent extends LayerComponent {
    constructor(map, group) {
        super(group || map);
    }
    ngOnInit() {
        // console.log(`creating ol.layer.Group instance with:`, this);
        this.instance = new Group(this);
        super.ngOnInit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: LayerGroupComponent, deps: [{ token: MapComponent }, { token: LayerGroupComponent, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: LayerGroupComponent, isStandalone: true, selector: "aol-layer-group", usesInheritance: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: LayerGroupComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-layer-group',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }, { type: LayerGroupComponent, decorators: [{
                    type: SkipSelf
                }, {
                    type: Optional
                }] }] });

class LayerVectorComponent extends LayerComponent {
    constructor(map, group) {
        super(group || map);
    }
    ngOnInit() {
        // console.log('creating ol.layer.Vector instance with:', this);
        this.instance = new Vector(this);
        super.ngOnInit();
    }
    ngOnChanges(changes) {
        super.ngOnChanges(changes);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: LayerVectorComponent, deps: [{ token: MapComponent }, { token: LayerGroupComponent, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: LayerVectorComponent, isStandalone: true, selector: "aol-layer-vector", inputs: { renderBuffer: "renderBuffer", style: "style", updateWhileAnimating: "updateWhileAnimating", updateWhileInteracting: "updateWhileInteracting" }, usesInheritance: true, usesOnChanges: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: LayerVectorComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-layer-vector',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }, { type: LayerGroupComponent, decorators: [{
                    type: Optional
                }] }], propDecorators: { renderBuffer: [{
                type: Input
            }], style: [{
                type: Input
            }], updateWhileAnimating: [{
                type: Input
            }], updateWhileInteracting: [{
                type: Input
            }] } });

class SourceVectorComponent extends SourceComponent {
    constructor(layer) {
        super(layer);
    }
    ngOnInit() {
        this.instance = new Vector$1(this);
        this.host.instance.setSource(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceVectorComponent, deps: [{ token: LayerVectorComponent, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: SourceVectorComponent, isStandalone: true, selector: "aol-source-vector", inputs: { overlaps: "overlaps", useSpatialIndex: "useSpatialIndex", wrapX: "wrapX", url: "url", format: "format", strategy: "strategy" }, providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceVectorComponent) }], usesInheritance: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceVectorComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-source-vector',
                    template: ` <ng-content></ng-content> `,
                    providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceVectorComponent) }],
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: LayerVectorComponent, decorators: [{
                    type: Host
                }] }], propDecorators: { overlaps: [{
                type: Input
            }], useSpatialIndex: [{
                type: Input
            }], wrapX: [{
                type: Input
            }], url: [{
                type: Input
            }], format: [{
                type: Input
            }], strategy: [{
                type: Input
            }] } });

class FeatureComponent {
    constructor(host) {
        this.host = host;
        this.componentType = 'feature';
    }
    ngOnInit() {
        this.instance = new Feature();
        if (this.id !== undefined) {
            this.instance.setId(this.id);
        }
        this.host.instance.addFeature(this.instance);
    }
    ngOnDestroy() {
        this.host.instance.removeFeature(this.instance);
    }
    ngOnChanges(changes) {
        if (this.instance) {
            this.instance.setId(this.id);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: FeatureComponent, deps: [{ token: SourceVectorComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: FeatureComponent, isStandalone: true, selector: "aol-feature", inputs: { id: "id" }, usesOnChanges: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: FeatureComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-feature',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: SourceVectorComponent }], propDecorators: { id: [{
                type: Input
            }] } });

// eslint-disable-next-line @angular-eslint/directive-class-suffix
class SimpleGeometryComponent {
    constructor(map, host) {
        this.map = map;
        this.host = host;
        this.componentType = 'simple-geometry';
    }
    ngOnInit() {
        this.host.instance.setGeometry(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SimpleGeometryComponent, deps: [{ token: MapComponent }, { token: FeatureComponent }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.3", type: SimpleGeometryComponent, isStandalone: true, inputs: { srid: "srid" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SimpleGeometryComponent, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: MapComponent }, { type: FeatureComponent }], propDecorators: { srid: [{
                type: Input
            }] } });

class ViewComponent {
    constructor(host) {
        this.host = host;
        this.zoomAnimation = false;
        this.olChange = new EventEmitter();
        this.changeCenter = new EventEmitter();
        this.changeResolution = new EventEmitter();
        this.changeRotation = new EventEmitter();
        this.olError = new EventEmitter();
        this.propertyChange = new EventEmitter();
        this.componentType = 'view';
    }
    ngOnInit() {
        // console.log('creating ol.View instance with: ', this);
        this.instance = new View(this);
        this.host.instance.setView(this.instance);
        this.instance.on('change', (event) => this.olChange.emit(event));
        this.instance.on('change:center', (event) => this.changeCenter.emit(event));
        this.instance.on('change:resolution', (event) => this.changeResolution.emit(event));
        this.instance.on('change:rotation', (event) => this.changeRotation.emit(event));
        this.instance.on('error', (event) => this.olError.emit(event));
        this.instance.on('propertychange', (event) => this.propertyChange.emit(event));
    }
    ngOnChanges(changes) {
        const properties = {};
        if (!this.instance) {
            return;
        }
        for (const key in changes) {
            if (changes.hasOwnProperty(key)) {
                switch (key) {
                    case 'zoom':
                        /** Work-around: setting the zoom via setProperties does not work. */
                        if (this.zoomAnimation) {
                            this.instance.animate({ zoom: changes[key].currentValue });
                        }
                        else {
                            this.instance.setZoom(changes[key].currentValue);
                        }
                        break;
                    case 'projection':
                        this.instance = new View(this);
                        this.host.instance.setView(this.instance);
                        break;
                    case 'center':
                        /** Work-around: setting the center via setProperties does not work. */
                        this.instance.setCenter(changes[key].currentValue);
                        break;
                    default:
                        break;
                }
                properties[key] = changes[key].currentValue;
            }
        }
        // console.log('changes detected in aol-view, setting new properties: ', properties);
        this.instance.setProperties(properties, false);
    }
    ngOnDestroy() {
        // console.log('removing aol-view');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ViewComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: ViewComponent, isStandalone: true, selector: "aol-view", inputs: { constrainRotation: "constrainRotation", enableRotation: "enableRotation", extent: "extent", maxResolution: "maxResolution", minResolution: "minResolution", maxZoom: "maxZoom", minZoom: "minZoom", resolution: "resolution", resolutions: "resolutions", rotation: "rotation", zoom: "zoom", zoomFactor: "zoomFactor", center: "center", projection: "projection", constrainOnlyCenter: "constrainOnlyCenter", smoothExtentConstraint: "smoothExtentConstraint", constrainResolution: "constrainResolution", smoothResolutionConstraint: "smoothResolutionConstraint", showFullExtent: "showFullExtent", multiWorld: "multiWorld", zoomAnimation: "zoomAnimation" }, outputs: { olChange: "olChange", changeCenter: "changeCenter", changeResolution: "changeResolution", changeRotation: "changeRotation", olError: "olError", propertyChange: "propertyChange" }, usesOnChanges: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ViewComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-view',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { constrainRotation: [{
                type: Input
            }], enableRotation: [{
                type: Input
            }], extent: [{
                type: Input
            }], maxResolution: [{
                type: Input
            }], minResolution: [{
                type: Input
            }], maxZoom: [{
                type: Input
            }], minZoom: [{
                type: Input
            }], resolution: [{
                type: Input
            }], resolutions: [{
                type: Input
            }], rotation: [{
                type: Input
            }], zoom: [{
                type: Input
            }], zoomFactor: [{
                type: Input
            }], center: [{
                type: Input
            }], projection: [{
                type: Input
            }], constrainOnlyCenter: [{
                type: Input
            }], smoothExtentConstraint: [{
                type: Input
            }], constrainResolution: [{
                type: Input
            }], smoothResolutionConstraint: [{
                type: Input
            }], showFullExtent: [{
                type: Input
            }], multiWorld: [{
                type: Input
            }], zoomAnimation: [{
                type: Input
            }], olChange: [{
                type: Output
            }], changeCenter: [{
                type: Output
            }], changeResolution: [{
                type: Output
            }], changeRotation: [{
                type: Output
            }], olError: [{
                type: Output
            }], propertyChange: [{
                type: Output
            }] } });

class GraticuleComponent {
    constructor(map) {
        this.map = map;
        this.componentType = 'graticule';
    }
    ngOnChanges(changes) {
        const properties = {};
        if (!this.instance) {
            return;
        }
        for (const key in changes) {
            if (changes.hasOwnProperty(key)) {
                properties[key] = changes[key].currentValue;
            }
        }
        if (properties) {
            this.instance = new Graticule(properties);
        }
        this.instance.setMap(this.map.instance);
    }
    ngAfterContentInit() {
        this.instance = new Graticule({
            strokeStyle: this.strokeStyle,
            showLabels: this.showLabels,
            lonLabelPosition: this.lonLabelPosition,
            latLabelPosition: this.latLabelPosition,
        });
        this.instance.setMap(this.map.instance);
    }
    ngOnDestroy() {
        this.instance.setMap(null);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: GraticuleComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: GraticuleComponent, isStandalone: true, selector: "aol-graticule", inputs: { strokeStyle: "strokeStyle", showLabels: "showLabels", lonLabelPosition: "lonLabelPosition", latLabelPosition: "latLabelPosition" }, usesOnChanges: true, ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: GraticuleComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-graticule',
                    template: '<ng-content></ng-content>',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { strokeStyle: [{
                type: Input
            }], showLabels: [{
                type: Input
            }], lonLabelPosition: [{
                type: Input
            }], latLabelPosition: [{
                type: Input
            }] } });

class LayerImageComponent extends LayerComponent {
    constructor(map, group) {
        super(group || map);
    }
    ngOnInit() {
        this.instance = new Image(this);
        super.ngOnInit();
    }
    ngOnChanges(changes) {
        super.ngOnChanges(changes);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: LayerImageComponent, deps: [{ token: MapComponent }, { token: LayerGroupComponent, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: LayerImageComponent, isStandalone: true, selector: "aol-layer-image", inputs: { opacity: "opacity", visible: "visible", extent: "extent", minResolution: "minResolution", maxResolution: "maxResolution", zIndex: "zIndex" }, usesInheritance: true, usesOnChanges: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: LayerImageComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-layer-image',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }, { type: LayerGroupComponent, decorators: [{
                    type: Optional
                }] }], propDecorators: { opacity: [{
                type: Input
            }], visible: [{
                type: Input
            }], extent: [{
                type: Input
            }], minResolution: [{
                type: Input
            }], maxResolution: [{
                type: Input
            }], zIndex: [{
                type: Input
            }] } });

class LayerTileComponent extends LayerComponent {
    constructor(map, group) {
        super(group || map);
    }
    ngOnInit() {
        // console.log('creating ol.layer.Tile instance with:', this);
        this.instance = new Tile(this);
        super.ngOnInit();
    }
    ngOnChanges(changes) {
        super.ngOnChanges(changes);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: LayerTileComponent, deps: [{ token: MapComponent }, { token: LayerGroupComponent, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: LayerTileComponent, isStandalone: true, selector: "aol-layer-tile", inputs: { preload: "preload", useInterimTilesOnError: "useInterimTilesOnError" }, usesInheritance: true, usesOnChanges: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: LayerTileComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-layer-tile',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }, { type: LayerGroupComponent, decorators: [{
                    type: Optional
                }] }], propDecorators: { preload: [{
                type: Input
            }], useInterimTilesOnError: [{
                type: Input
            }] } });

class LayerVectorTileComponent extends LayerComponent {
    constructor(map, group) {
        super(group || map);
    }
    ngOnInit() {
        // console.log('creating ol.layer.VectorTile instance with:', this);
        this.instance = new VectorTile(this);
        super.ngOnInit();
    }
    ngOnChanges(changes) {
        super.ngOnChanges(changes);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: LayerVectorTileComponent, deps: [{ token: MapComponent }, { token: LayerGroupComponent, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: LayerVectorTileComponent, isStandalone: true, selector: "aol-layer-vectortile", inputs: { renderBuffer: "renderBuffer", renderMode: "renderMode", renderOrder: "renderOrder", style: "style", updateWhileAnimating: "updateWhileAnimating", updateWhileInteracting: "updateWhileInteracting", visible: "visible" }, usesInheritance: true, usesOnChanges: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: LayerVectorTileComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-layer-vectortile',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }, { type: LayerGroupComponent, decorators: [{
                    type: Optional
                }] }], propDecorators: { renderBuffer: [{
                type: Input
            }], renderMode: [{
                type: Input
            }], renderOrder: [{
                type: Input
            }], style: [{
                type: Input
            }], updateWhileAnimating: [{
                type: Input
            }], updateWhileInteracting: [{
                type: Input
            }], visible: [{
                type: Input
            }] } });

class TileGridComponent {
    ngOnInit() {
        if (!this.resolutions) {
            this.instance = createXYZ(this);
        }
        else {
            this.instance = new TileGrid(this);
        }
    }
    ngOnChanges(changes) {
        if (!this.resolutions) {
            this.instance = createXYZ(this);
        }
        else {
            this.instance = new TileGrid(this);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: TileGridComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: TileGridComponent, isStandalone: true, selector: "aol-tilegrid", inputs: { extent: "extent", maxZoom: "maxZoom", minZoom: "minZoom", tileSize: "tileSize", origin: "origin", resolutions: "resolutions" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: TileGridComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-tilegrid',
                    template: '',
                    standalone: true,
                }]
        }], propDecorators: { extent: [{
                type: Input
            }], maxZoom: [{
                type: Input
            }], minZoom: [{
                type: Input
            }], tileSize: [{
                type: Input
            }], origin: [{
                type: Input
            }], resolutions: [{
                type: Input
            }] } });

class SourceXYZComponent extends SourceComponent {
    constructor(layer) {
        super(layer);
        this.layer = layer;
        this.tileLoadStart = new EventEmitter();
        this.tileLoadEnd = new EventEmitter();
        this.tileLoadError = new EventEmitter();
    }
    ngAfterContentInit() {
        if (this.tileGridXYZ) {
            this.tileGrid = this.tileGridXYZ.instance;
        }
        this.init();
    }
    ngOnChanges(changes) {
        const properties = {};
        if (!this.instance) {
            return;
        }
        for (const key in changes) {
            if (changes.hasOwnProperty(key)) {
                properties[key] = changes[key].currentValue;
            }
        }
        this.instance.setProperties(properties, false);
        if (changes.hasOwnProperty('url')) {
            this.init();
        }
    }
    init() {
        this.instance = new XYZ(this);
        this.instance.on('tileloadstart', (event) => this.tileLoadStart.emit(event));
        this.instance.on('tileloadend', (event) => this.tileLoadEnd.emit(event));
        this.instance.on('tileloaderror', (event) => this.tileLoadError.emit(event));
        this.register(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceXYZComponent, deps: [{ token: LayerTileComponent, host: true, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: SourceXYZComponent, isStandalone: true, selector: "aol-source-xyz", inputs: { cacheSize: "cacheSize", crossOrigin: "crossOrigin", opaque: "opaque", projection: "projection", reprojectionErrorThreshold: "reprojectionErrorThreshold", minZoom: "minZoom", maxZoom: "maxZoom", tileGrid: "tileGrid", tileLoadFunction: "tileLoadFunction", tilePixelRatio: "tilePixelRatio", tileSize: "tileSize", tileUrlFunction: "tileUrlFunction", url: "url", urls: "urls", wrapX: "wrapX" }, outputs: { tileLoadStart: "tileLoadStart", tileLoadEnd: "tileLoadEnd", tileLoadError: "tileLoadError" }, providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceXYZComponent) }], queries: [{ propertyName: "tileGridXYZ", first: true, predicate: TileGridComponent, descendants: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceXYZComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-source-xyz',
                    template: ` <ng-content></ng-content> `,
                    providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceXYZComponent) }],
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: LayerTileComponent, decorators: [{
                    type: Optional
                }, {
                    type: Host
                }] }], propDecorators: { cacheSize: [{
                type: Input
            }], crossOrigin: [{
                type: Input
            }], opaque: [{
                type: Input
            }], projection: [{
                type: Input
            }], reprojectionErrorThreshold: [{
                type: Input
            }], minZoom: [{
                type: Input
            }], maxZoom: [{
                type: Input
            }], tileGrid: [{
                type: Input
            }], tileLoadFunction: [{
                type: Input
            }], tilePixelRatio: [{
                type: Input
            }], tileSize: [{
                type: Input
            }], tileUrlFunction: [{
                type: Input
            }], url: [{
                type: Input
            }], urls: [{
                type: Input
            }], wrapX: [{
                type: Input
            }], tileGridXYZ: [{
                type: ContentChild,
                args: [TileGridComponent, { static: false }]
            }], tileLoadStart: [{
                type: Output
            }], tileLoadEnd: [{
                type: Output
            }], tileLoadError: [{
                type: Output
            }] } });

class SourceOsmComponent extends SourceXYZComponent {
    constructor(layer) {
        super(layer);
        this.layer = layer;
        this.tileLoadStart = new EventEmitter();
        this.tileLoadEnd = new EventEmitter();
        this.tileLoadError = new EventEmitter();
    }
    ngAfterContentInit() {
        if (this.tileGridXYZ) {
            this.tileGrid = this.tileGridXYZ.instance;
        }
        this.instance = new OSM(this);
        this.instance.on('tileloadstart', (event) => this.tileLoadStart.emit(event));
        this.instance.on('tileloadend', (event) => this.tileLoadEnd.emit(event));
        this.instance.on('tileloaderror', (event) => this.tileLoadError.emit(event));
        this.register(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceOsmComponent, deps: [{ token: LayerTileComponent, host: true, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: SourceOsmComponent, isStandalone: true, selector: "aol-source-osm", inputs: { attributions: "attributions", cacheSize: "cacheSize", crossOrigin: "crossOrigin", maxZoom: "maxZoom", opaque: "opaque", reprojectionErrorThreshold: "reprojectionErrorThreshold", tileLoadFunction: "tileLoadFunction", url: "url", wrapX: "wrapX" }, outputs: { tileLoadStart: "tileLoadStart", tileLoadEnd: "tileLoadEnd", tileLoadError: "tileLoadError" }, providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceOsmComponent) }], usesInheritance: true, ngImport: i0, template: ` <div class="aol-source-osm"></div> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceOsmComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-source-osm',
                    template: ` <div class="aol-source-osm"></div> `,
                    providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceOsmComponent) }],
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: LayerTileComponent, decorators: [{
                    type: Optional
                }, {
                    type: Host
                }] }], propDecorators: { attributions: [{
                type: Input
            }], cacheSize: [{
                type: Input
            }], crossOrigin: [{
                type: Input
            }], maxZoom: [{
                type: Input
            }], opaque: [{
                type: Input
            }], reprojectionErrorThreshold: [{
                type: Input
            }], tileLoadFunction: [{
                type: Input
            }], url: [{
                type: Input
            }], wrapX: [{
                type: Input
            }], tileLoadStart: [{
                type: Output
            }], tileLoadEnd: [{
                type: Output
            }], tileLoadError: [{
                type: Output
            }] } });

class SourceBingmapsComponent extends SourceComponent {
    constructor(layer) {
        super(layer);
        this.imagerySet = 'Aerial';
    }
    ngOnInit() {
        this.instance = new BingMaps(this);
        this.host.instance.setSource(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceBingmapsComponent, deps: [{ token: LayerTileComponent, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: SourceBingmapsComponent, isStandalone: true, selector: "aol-source-bingmaps", inputs: { cacheSize: "cacheSize", hidpi: "hidpi", culture: "culture", key: "key", imagerySet: "imagerySet", maxZoom: "maxZoom", reprojectionErrorThreshold: "reprojectionErrorThreshold", tileLoadFunction: "tileLoadFunction", wrapX: "wrapX" }, providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceBingmapsComponent) }], usesInheritance: true, ngImport: i0, template: ` <div class="aol-source-bingmaps"></div> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceBingmapsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-source-bingmaps',
                    template: ` <div class="aol-source-bingmaps"></div> `,
                    providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceBingmapsComponent) }],
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: LayerTileComponent, decorators: [{
                    type: Host
                }] }], propDecorators: { cacheSize: [{
                type: Input
            }], hidpi: [{
                type: Input
            }], culture: [{
                type: Input
            }], key: [{
                type: Input
            }], imagerySet: [{
                type: Input
            }], maxZoom: [{
                type: Input
            }], reprojectionErrorThreshold: [{
                type: Input
            }], tileLoadFunction: [{
                type: Input
            }], wrapX: [{
                type: Input
            }] } });

class SourceClusterComponent extends SourceComponent {
    constructor(layer) {
        super(layer);
    }
    ngAfterContentInit() {
        this.source = this.sourceVectorComponent.instance;
        this.instance = new Cluster(this);
        this.host.instance.setSource(this.instance);
    }
    ngOnChanges(changes) {
        if (this.instance && changes.hasOwnProperty('distance')) {
            this.instance.setDistance(this.distance);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceClusterComponent, deps: [{ token: LayerVectorComponent, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: SourceClusterComponent, isStandalone: true, selector: "aol-source-cluster", inputs: { distance: "distance", geometryFunction: "geometryFunction", wrapX: "wrapX" }, providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceClusterComponent) }], queries: [{ propertyName: "sourceVectorComponent", first: true, predicate: SourceVectorComponent, descendants: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceClusterComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-source-cluster',
                    template: ` <ng-content></ng-content> `,
                    providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceClusterComponent) }],
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: LayerVectorComponent, decorators: [{
                    type: Host
                }] }], propDecorators: { distance: [{
                type: Input
            }], geometryFunction: [{
                type: Input
            }], wrapX: [{
                type: Input
            }], sourceVectorComponent: [{
                type: ContentChild,
                args: [SourceVectorComponent, { static: false }]
            }] } });

class TileGridWMTSComponent extends TileGridComponent {
    ngOnInit() {
        this.instance = new WMTS(this);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: TileGridWMTSComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: TileGridWMTSComponent, isStandalone: true, selector: "aol-tilegrid-wmts", inputs: { origin: "origin", origins: "origins", resolutions: "resolutions", matrixIds: "matrixIds", sizes: "sizes", tileSizes: "tileSizes", widths: "widths" }, usesInheritance: true, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: TileGridWMTSComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-tilegrid-wmts',
                    template: '',
                    standalone: true,
                }]
        }], propDecorators: { origin: [{
                type: Input
            }], origins: [{
                type: Input
            }], resolutions: [{
                type: Input
            }], matrixIds: [{
                type: Input
            }], sizes: [{
                type: Input
            }], tileSizes: [{
                type: Input
            }], widths: [{
                type: Input
            }] } });

class SourceTileWMTSComponent extends SourceComponent {
    constructor(layer) {
        super(layer);
        this.tileLoadStart = new EventEmitter();
        this.tileLoadEnd = new EventEmitter();
        this.tileLoadError = new EventEmitter();
    }
    ngOnChanges(changes) {
        const properties = {};
        if (!this.instance) {
            return;
        }
        for (const key in changes) {
            if (changes.hasOwnProperty(key)) {
                switch (key) {
                    case 'url':
                        this.url = changes[key].currentValue;
                        this.setLayerSource();
                        break;
                    default:
                        break;
                }
                properties[key] = changes[key].currentValue;
            }
        }
        this.instance.setProperties(properties, false);
    }
    setLayerSource() {
        this.instance = new WMTS$1(this);
        this.instance.on('tileloadstart', (event) => this.tileLoadStart.emit(event));
        this.instance.on('tileloadend', (event) => this.tileLoadEnd.emit(event));
        this.instance.on('tileloaderror', (event) => this.tileLoadError.emit(event));
        this.host.instance.setSource(this.instance);
    }
    ngAfterContentInit() {
        if (this.tileGridWMTS) {
            this.tileGrid = this.tileGridWMTS.instance;
            this.setLayerSource();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceTileWMTSComponent, deps: [{ token: LayerTileComponent, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: SourceTileWMTSComponent, isStandalone: true, selector: "aol-source-tilewmts", inputs: { cacheSize: "cacheSize", crossOrigin: "crossOrigin", tileGrid: "tileGrid", projection: "projection", reprojectionErrorThreshold: "reprojectionErrorThreshold", requestEncoding: "requestEncoding", layer: "layer", style: "style", tileClass: "tileClass", tilePixelRatio: "tilePixelRatio", version: "version", format: "format", matrixSet: "matrixSet", dimensions: "dimensions", url: "url", tileLoadFunction: "tileLoadFunction", urls: "urls", wrapX: "wrapX" }, outputs: { tileLoadStart: "tileLoadStart", tileLoadEnd: "tileLoadEnd", tileLoadError: "tileLoadError" }, providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceTileWMTSComponent) }], queries: [{ propertyName: "tileGridWMTS", first: true, predicate: TileGridWMTSComponent, descendants: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceTileWMTSComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-source-tilewmts',
                    template: ` <ng-content></ng-content> `,
                    providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceTileWMTSComponent) }],
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: LayerTileComponent, decorators: [{
                    type: Host
                }] }], propDecorators: { cacheSize: [{
                type: Input
            }], crossOrigin: [{
                type: Input
            }], tileGrid: [{
                type: Input
            }], projection: [{
                type: Input
            }], reprojectionErrorThreshold: [{
                type: Input
            }], requestEncoding: [{
                type: Input
            }], layer: [{
                type: Input
            }], style: [{
                type: Input
            }], tileClass: [{
                type: Input
            }], tilePixelRatio: [{
                type: Input
            }], version: [{
                type: Input
            }], format: [{
                type: Input
            }], matrixSet: [{
                type: Input
            }], dimensions: [{
                type: Input
            }], url: [{
                type: Input
            }], tileLoadFunction: [{
                type: Input
            }], urls: [{
                type: Input
            }], wrapX: [{
                type: Input
            }], tileLoadStart: [{
                type: Output
            }], tileLoadEnd: [{
                type: Output
            }], tileLoadError: [{
                type: Output
            }], tileGridWMTS: [{
                type: ContentChild,
                args: [TileGridWMTSComponent, { static: false }]
            }] } });

class FormatComponent {
    constructor() {
        this.componentType = 'format';
    }
}

class SourceVectorTileComponent extends SourceComponent {
    constructor(layer) {
        super(layer);
    }
    /* need the children to construct the OL3 object */
    ngAfterContentInit() {
        this.format = this.formatComponent.instance;
        this.tileGrid = this.tileGridComponent.instance;
        // console.log('creating ol.source.VectorTile instance with:', this);
        this.instance = new VectorTile$1(this);
        this.host.instance.setSource(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceVectorTileComponent, deps: [{ token: LayerVectorTileComponent, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: SourceVectorTileComponent, isStandalone: true, selector: "aol-source-vectortile", inputs: { cacheSize: "cacheSize", overlaps: "overlaps", projection: "projection", tilePixelRatio: "tilePixelRatio", tileUrlFunction: "tileUrlFunction", url: "url", urls: "urls", wrapX: "wrapX" }, providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceVectorTileComponent) }], queries: [{ propertyName: "formatComponent", first: true, predicate: FormatComponent, descendants: true }, { propertyName: "tileGridComponent", first: true, predicate: TileGridComponent, descendants: true }], usesInheritance: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceVectorTileComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-source-vectortile',
                    template: ` <ng-content></ng-content> `,
                    providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceVectorTileComponent) }],
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: LayerVectorTileComponent, decorators: [{
                    type: Host
                }] }], propDecorators: { cacheSize: [{
                type: Input
            }], overlaps: [{
                type: Input
            }], projection: [{
                type: Input
            }], tilePixelRatio: [{
                type: Input
            }], tileUrlFunction: [{
                type: Input
            }], url: [{
                type: Input
            }], urls: [{
                type: Input
            }], wrapX: [{
                type: Input
            }], formatComponent: [{
                type: ContentChild,
                args: [FormatComponent, { static: false }]
            }], tileGridComponent: [{
                type: ContentChild,
                args: [TileGridComponent, { static: false }]
            }] } });

class SourceTileWMSComponent extends SourceComponent {
    constructor(layer) {
        super(layer);
    }
    ngOnInit() {
        this.instance = new TileWMS(this);
        this.host.instance.setSource(this.instance);
    }
    ngOnChanges(changes) {
        if (this.instance && changes.hasOwnProperty('params')) {
            this.instance.updateParams(this.params);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceTileWMSComponent, deps: [{ token: LayerTileComponent, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: SourceTileWMSComponent, isStandalone: true, selector: "aol-source-tilewms", inputs: { cacheSize: "cacheSize", crossOrigin: "crossOrigin", gutter: "gutter", hidpi: "hidpi", params: "params", projection: "projection", reprojectionErrorThreshold: "reprojectionErrorThreshold", serverType: "serverType", tileGrid: "tileGrid", tileLoadFunction: "tileLoadFunction", url: "url", urls: "urls", wrapX: "wrapX" }, providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceTileWMSComponent) }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceTileWMSComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-source-tilewms',
                    template: ` <ng-content></ng-content> `,
                    providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceTileWMSComponent) }],
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: LayerTileComponent, decorators: [{
                    type: Host
                }] }], propDecorators: { cacheSize: [{
                type: Input
            }], crossOrigin: [{
                type: Input
            }], gutter: [{
                type: Input
            }], hidpi: [{
                type: Input
            }], params: [{
                type: Input
            }], projection: [{
                type: Input
            }], reprojectionErrorThreshold: [{
                type: Input
            }], serverType: [{
                type: Input
            }], tileGrid: [{
                type: Input
            }], tileLoadFunction: [{
                type: Input
            }], url: [{
                type: Input
            }], urls: [{
                type: Input
            }], wrapX: [{
                type: Input
            }] } });

class SourceTileJSONComponent extends SourceComponent {
    constructor(layer) {
        super(layer);
    }
    ngOnInit() {
        this.instance = new TileJSON(this);
        this.host.instance.setSource(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceTileJSONComponent, deps: [{ token: LayerTileComponent, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: SourceTileJSONComponent, isStandalone: true, selector: "aol-source-tilejson", inputs: { url: "url" }, providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceTileJSONComponent) }], usesInheritance: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceTileJSONComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-source-tilejson',
                    template: ` <ng-content></ng-content> `,
                    providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceTileJSONComponent) }],
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: LayerTileComponent, decorators: [{
                    type: Host
                }] }], propDecorators: { url: [{
                type: Input
            }] } });

class SourceGeoJSONComponent extends SourceComponent {
    constructor(layer) {
        super(layer);
    }
    ngOnInit() {
        this.format = new GeoJSON(this);
        this.instance = new Vector$1(this);
        this.host.instance.setSource(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceGeoJSONComponent, deps: [{ token: LayerVectorComponent, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: SourceGeoJSONComponent, isStandalone: true, selector: "aol-source-geojson", inputs: { defaultDataProjection: "defaultDataProjection", featureProjection: "featureProjection", geometryName: "geometryName", url: "url" }, providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceGeoJSONComponent) }], usesInheritance: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceGeoJSONComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-source-geojson',
                    template: ` <ng-content></ng-content> `,
                    providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceGeoJSONComponent) }],
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: LayerVectorComponent, decorators: [{
                    type: Host
                }] }], propDecorators: { defaultDataProjection: [{
                type: Input
            }], featureProjection: [{
                type: Input
            }], geometryName: [{
                type: Input
            }], url: [{
                type: Input
            }] } });

class SourceImageStaticComponent extends SourceComponent {
    constructor(layer) {
        super(layer);
        this.imageLoadStart = new EventEmitter();
        this.imageLoadEnd = new EventEmitter();
        this.imageLoadError = new EventEmitter();
    }
    setLayerSource() {
        this.instance = new ImageStatic(this);
        this.host.instance.setSource(this.instance);
        this.instance.on('imageloadstart', (event) => this.imageLoadStart.emit(event));
        this.instance.on('imageloadend', (event) => this.imageLoadEnd.emit(event));
        this.instance.on('imageloaderror', (event) => this.imageLoadError.emit(event));
    }
    ngOnInit() {
        this.setLayerSource();
    }
    ngOnChanges(changes) {
        const properties = {};
        if (!this.instance) {
            return;
        }
        for (const key in changes) {
            if (changes.hasOwnProperty(key)) {
                switch (key) {
                    case 'url':
                        this.url = changes[key].currentValue;
                        this.setLayerSource();
                        break;
                    default:
                        break;
                }
                properties[key] = changes[key].currentValue;
            }
        }
        this.instance.setProperties(properties, false);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceImageStaticComponent, deps: [{ token: LayerImageComponent, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: SourceImageStaticComponent, isStandalone: true, selector: "aol-source-imagestatic", inputs: { projection: "projection", imageExtent: "imageExtent", url: "url", attributions: "attributions", crossOrigin: "crossOrigin", imageLoadFunction: "imageLoadFunction", imageSize: "imageSize" }, outputs: { imageLoadStart: "imageLoadStart", imageLoadEnd: "imageLoadEnd", imageLoadError: "imageLoadError" }, providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceImageStaticComponent) }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceImageStaticComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-source-imagestatic',
                    template: ` <ng-content></ng-content> `,
                    providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceImageStaticComponent) }],
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: LayerImageComponent, decorators: [{
                    type: Host
                }] }], propDecorators: { projection: [{
                type: Input
            }], imageExtent: [{
                type: Input
            }], url: [{
                type: Input
            }], attributions: [{
                type: Input
            }], crossOrigin: [{
                type: Input
            }], imageLoadFunction: [{
                type: Input
            }], imageSize: [{
                type: Input
            }], imageLoadStart: [{
                type: Output
            }], imageLoadEnd: [{
                type: Output
            }], imageLoadError: [{
                type: Output
            }] } });

class SourceImageWMSComponent extends SourceComponent {
    constructor(layer) {
        super(layer);
        this.imageLoadStart = new EventEmitter();
        this.imageLoadEnd = new EventEmitter();
        this.imageLoadError = new EventEmitter();
    }
    ngOnInit() {
        this.instance = new ImageWMS(this);
        this.host.instance.setSource(this.instance);
        this.instance.on('imageloadstart', (event) => this.imageLoadStart.emit(event));
        this.instance.on('imageloadend', (event) => this.imageLoadEnd.emit(event));
        this.instance.on('imageloaderror', (event) => this.imageLoadError.emit(event));
    }
    ngOnChanges(changes) {
        if (this.instance && changes.hasOwnProperty('params')) {
            this.instance.updateParams(this.params);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceImageWMSComponent, deps: [{ token: LayerImageComponent, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: SourceImageWMSComponent, isStandalone: true, selector: "aol-source-imagewms", inputs: { attributions: "attributions", crossOrigin: "crossOrigin", hidpi: "hidpi", serverType: "serverType", imageLoadFunction: "imageLoadFunction", params: "params", projection: "projection", ratio: "ratio", resolutions: "resolutions", url: "url" }, outputs: { imageLoadStart: "imageLoadStart", imageLoadEnd: "imageLoadEnd", imageLoadError: "imageLoadError" }, providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceImageWMSComponent) }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceImageWMSComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-source-imagewms',
                    template: ` <ng-content></ng-content> `,
                    providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceImageWMSComponent) }],
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: LayerImageComponent, decorators: [{
                    type: Host
                }] }], propDecorators: { attributions: [{
                type: Input
            }], crossOrigin: [{
                type: Input
            }], hidpi: [{
                type: Input
            }], serverType: [{
                type: Input
            }], imageLoadFunction: [{
                type: Input
            }], params: [{
                type: Input
            }], projection: [{
                type: Input
            }], ratio: [{
                type: Input
            }], resolutions: [{
                type: Input
            }], url: [{
                type: Input
            }], imageLoadStart: [{
                type: Output
            }], imageLoadEnd: [{
                type: Output
            }], imageLoadError: [{
                type: Output
            }] } });

class SourceImageArcGISRestComponent extends SourceComponent {
    constructor(layer) {
        super(layer);
        this.ratio = 1.5;
        this.imageLoadStart = new EventEmitter();
        this.imageLoadEnd = new EventEmitter();
        this.imageLoadError = new EventEmitter();
    }
    ngOnInit() {
        this.instance = new ImageArcGISRest(this);
        this.host.instance.setSource(this.instance);
        this.instance.on('imageloadstart', (event) => this.imageLoadStart.emit(event));
        this.instance.on('imageloadend', (event) => this.imageLoadEnd.emit(event));
        this.instance.on('imageloaderror', (event) => this.imageLoadError.emit(event));
    }
    ngOnChanges(changes) {
        if (this.instance && changes.hasOwnProperty('params')) {
            this.instance.updateParams(this.params);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceImageArcGISRestComponent, deps: [{ token: LayerImageComponent, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: SourceImageArcGISRestComponent, isStandalone: true, selector: "aol-source-imagearcgisrest", inputs: { projection: "projection", url: "url", attributions: "attributions", crossOrigin: "crossOrigin", imageLoadFunction: "imageLoadFunction", params: "params", ratio: "ratio", resolutions: "resolutions", wrapX: "wrapX" }, outputs: { imageLoadStart: "imageLoadStart", imageLoadEnd: "imageLoadEnd", imageLoadError: "imageLoadError" }, providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceImageArcGISRestComponent) }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceImageArcGISRestComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-source-imagearcgisrest',
                    template: ` <ng-content></ng-content> `,
                    providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceImageArcGISRestComponent) }],
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: LayerImageComponent, decorators: [{
                    type: Host
                }] }], propDecorators: { projection: [{
                type: Input
            }], url: [{
                type: Input
            }], attributions: [{
                type: Input
            }], crossOrigin: [{
                type: Input
            }], imageLoadFunction: [{
                type: Input
            }], params: [{
                type: Input
            }], ratio: [{
                type: Input
            }], resolutions: [{
                type: Input
            }], wrapX: [{
                type: Input
            }], imageLoadStart: [{
                type: Output
            }], imageLoadEnd: [{
                type: Output
            }], imageLoadError: [{
                type: Output
            }] } });

class SourceRasterComponent extends SourceComponent {
    set source(sourceComponent) {
        this.sources = [sourceComponent.instance];
        if (this.instance) {
            // Openlayer doesn't handle sources update. Just recreate Raster instance.
            this.init();
        }
    }
    constructor(layer) {
        super(layer);
        this.beforeOperations = new EventEmitter();
        this.afterOperations = new EventEmitter();
        this.sources = [];
    }
    ngAfterContentInit() {
        this.init();
    }
    init() {
        this.instance = new Raster(this);
        this.instance.on('beforeoperations', (event) => this.beforeOperations.emit(event));
        this.instance.on('afteroperations', (event) => this.afterOperations.emit(event));
        this.register(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceRasterComponent, deps: [{ token: LayerImageComponent, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: SourceRasterComponent, isStandalone: true, selector: "aol-source-raster", inputs: { operation: "operation", threads: "threads", lib: "lib", operationType: "operationType" }, outputs: { beforeOperations: "beforeOperations", afterOperations: "afterOperations" }, providers: [
            {
                provide: SourceComponent,
                useExisting: forwardRef(() => SourceRasterComponent),
            },
        ], queries: [{ propertyName: "source", first: true, predicate: SourceComponent, descendants: true }], usesInheritance: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceRasterComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-source-raster',
                    template: ` <ng-content></ng-content> `,
                    providers: [
                        {
                            provide: SourceComponent,
                            useExisting: forwardRef(() => SourceRasterComponent),
                        },
                    ],
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: LayerImageComponent, decorators: [{
                    type: Host
                }] }], propDecorators: { operation: [{
                type: Input
            }], threads: [{
                type: Input
            }], lib: [{
                type: Input
            }], operationType: [{
                type: Input
            }], beforeOperations: [{
                type: Output
            }], afterOperations: [{
                type: Output
            }], source: [{
                type: ContentChild,
                args: [SourceComponent, { static: false }]
            }] } });

class GeometryCircleComponent extends SimpleGeometryComponent {
    get radius() {
        return this.instance.getRadius();
    }
    set radius(radius) {
        this.instance.setRadius(radius);
    }
    constructor(map, host) {
        super(map, host);
        this.componentType = 'geometry-circle';
        // defaulting coordinates to [0,0]. To be overridden in child component.
        this.instance = new Circle([0, 0]);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: GeometryCircleComponent, deps: [{ token: MapComponent }, { token: FeatureComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: GeometryCircleComponent, isStandalone: true, selector: "aol-geometry-circle", inputs: { radius: "radius" }, usesInheritance: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: GeometryCircleComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-geometry-circle',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }, { type: FeatureComponent }], propDecorators: { radius: [{
                type: Input
            }] } });

class GeometryLinestringComponent extends SimpleGeometryComponent {
    constructor(map, host) {
        super(map, host);
        this.componentType = 'geometry-linestring';
    }
    ngOnInit() {
        this.instance = new LineString([
            [0, 0],
            [1, 1],
        ]);
        super.ngOnInit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: GeometryLinestringComponent, deps: [{ token: MapComponent }, { token: FeatureComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: GeometryLinestringComponent, isStandalone: true, selector: "aol-geometry-linestring", usesInheritance: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: GeometryLinestringComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-geometry-linestring',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }, { type: FeatureComponent }] });

class GeometryMultiLinestringComponent extends SimpleGeometryComponent {
    constructor(map, host) {
        super(map, host);
        this.componentType = 'geometry-multilinestring';
    }
    ngOnInit() {
        this.instance = new MultiLineString([
            [
                [0, 0],
                [1, 1],
            ],
        ]);
        super.ngOnInit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: GeometryMultiLinestringComponent, deps: [{ token: MapComponent }, { token: FeatureComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: GeometryMultiLinestringComponent, isStandalone: true, selector: "aol-geometry-multilinestring", usesInheritance: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: GeometryMultiLinestringComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-geometry-multilinestring',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }, { type: FeatureComponent }] });

class GeometryMultiPointComponent extends SimpleGeometryComponent {
    constructor(map, host) {
        super(map, host);
        this.componentType = 'geometry-multipoint';
    }
    ngOnInit() {
        this.instance = new MultiPoint([
            [0, 0],
            [1, 1],
        ]);
        super.ngOnInit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: GeometryMultiPointComponent, deps: [{ token: MapComponent }, { token: FeatureComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: GeometryMultiPointComponent, isStandalone: true, selector: "aol-geometry-multipoint", usesInheritance: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: GeometryMultiPointComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-geometry-multipoint',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }, { type: FeatureComponent }] });

class GeometryMultiPolygonComponent extends SimpleGeometryComponent {
    constructor(map, host) {
        super(map, host);
        this.componentType = 'geometry-multipolygon';
    }
    ngOnInit() {
        this.instance = new MultiPolygon([
            [
                [
                    [0, 0],
                    [1, 1],
                    [0, 1],
                ],
            ],
        ]);
        super.ngOnInit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: GeometryMultiPolygonComponent, deps: [{ token: MapComponent }, { token: FeatureComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: GeometryMultiPolygonComponent, isStandalone: true, selector: "aol-geometry-multipolygon", usesInheritance: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: GeometryMultiPolygonComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-geometry-multipolygon',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }, { type: FeatureComponent }] });

class GeometryPointComponent extends SimpleGeometryComponent {
    constructor(map, host) {
        super(map, host);
        this.componentType = 'geometry-point';
    }
    ngOnInit() {
        this.instance = new Point([0, 0]);
        super.ngOnInit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: GeometryPointComponent, deps: [{ token: MapComponent }, { token: FeatureComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: GeometryPointComponent, isStandalone: true, selector: "aol-geometry-point", usesInheritance: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: GeometryPointComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-geometry-point',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }, { type: FeatureComponent }] });

class GeometryPolygonComponent extends SimpleGeometryComponent {
    constructor(map, host) {
        super(map, host);
        this.componentType = 'geometry-polygon';
    }
    ngOnInit() {
        this.instance = new Polygon([
            [
                [0, 0],
                [1, 1],
                [0, 1],
            ],
        ]);
        super.ngOnInit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: GeometryPolygonComponent, deps: [{ token: MapComponent }, { token: FeatureComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: GeometryPolygonComponent, isStandalone: true, selector: "aol-geometry-polygon", usesInheritance: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: GeometryPolygonComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-geometry-polygon',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }, { type: FeatureComponent }] });

class ContentComponent {
    constructor(elementRef) {
        this.elementRef = elementRef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ContentComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: ContentComponent, isStandalone: true, selector: "aol-content", ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ContentComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-content',
                    template: '<ng-content></ng-content>',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }] });

class OverlayComponent {
    constructor(map) {
        this.map = map;
        this.componentType = 'overlay';
    }
    ngOnInit() {
        if (this.content) {
            this.element = this.content.elementRef.nativeElement;
            this.instance = new Overlay(this);
            this.map.instance.addOverlay(this.instance);
        }
    }
    ngOnDestroy() {
        if (this.instance) {
            this.map.instance.removeOverlay(this.instance);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: OverlayComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: OverlayComponent, isStandalone: true, selector: "aol-overlay", inputs: { id: "id", offset: "offset", positioning: "positioning", stopEvent: "stopEvent", insertFirst: "insertFirst", autoPan: "autoPan", autoPanAnimation: "autoPanAnimation", autoPanMargin: "autoPanMargin" }, queries: [{ propertyName: "content", first: true, predicate: ContentComponent, descendants: true, static: true }], ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: OverlayComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-overlay',
                    template: '<ng-content></ng-content>',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { content: [{
                type: ContentChild,
                args: [ContentComponent, { static: true }]
            }], id: [{
                type: Input
            }], offset: [{
                type: Input
            }], positioning: [{
                type: Input
            }], stopEvent: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], autoPan: [{
                type: Input
            }], autoPanAnimation: [{
                type: Input
            }], autoPanMargin: [{
                type: Input
            }] } });

class CoordinateComponent {
    constructor(map, viewHost, geometryPointHost, geometryCircleHost, overlayHost) {
        this.map = map;
        this.srid = 'EPSG:3857';
        this.mapSrid = 'EPSG:3857';
        // console.log('instancing aol-coordinate');
        if (geometryPointHost !== null) {
            this.host = geometryPointHost;
        }
        else if (geometryCircleHost !== null) {
            this.host = geometryCircleHost;
        }
        else if (viewHost !== null) {
            this.host = viewHost;
        }
        else if (overlayHost !== null) {
            this.host = overlayHost;
        }
    }
    ngOnInit() {
        this.map.instance.on('change:view', (e) => this.onMapViewChanged(e));
        this.mapSrid = this.map.instance.getView().getProjection().getCode();
        this.transformCoordinates();
    }
    ngOnChanges(changes) {
        this.transformCoordinates();
    }
    onMapViewChanged(event) {
        this.mapSrid = event.target.get(event.key).getProjection().getCode();
        this.transformCoordinates();
    }
    transformCoordinates() {
        let transformedCoordinates;
        if (this.srid === this.mapSrid) {
            transformedCoordinates = [this.x, this.y];
        }
        else {
            transformedCoordinates = transform([this.x, this.y], this.srid, this.mapSrid);
        }
        switch (this.host.componentType) {
            case 'geometry-point':
                this.host.instance.setCoordinates(transformedCoordinates);
                break;
            case 'geometry-circle':
            case 'view':
                this.host.instance.setCenter(transformedCoordinates);
                break;
            case 'overlay':
                this.host.instance.setPosition(transformedCoordinates);
                break;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: CoordinateComponent, deps: [{ token: MapComponent }, { token: ViewComponent, optional: true }, { token: GeometryPointComponent, optional: true }, { token: GeometryCircleComponent, optional: true }, { token: OverlayComponent, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: CoordinateComponent, isStandalone: true, selector: "aol-coordinate", inputs: { x: "x", y: "y", srid: "srid" }, usesOnChanges: true, ngImport: i0, template: ` <div class="aol-coordinate"></div> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: CoordinateComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-coordinate',
                    template: ` <div class="aol-coordinate"></div> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }, { type: ViewComponent, decorators: [{
                    type: Optional
                }] }, { type: GeometryPointComponent, decorators: [{
                    type: Optional
                }] }, { type: GeometryCircleComponent, decorators: [{
                    type: Optional
                }] }, { type: OverlayComponent, decorators: [{
                    type: Optional
                }] }], propDecorators: { x: [{
                type: Input
            }], y: [{
                type: Input
            }], srid: [{
                type: Input
            }] } });

class CollectionCoordinatesComponent {
    constructor(map, geometryLinestring, geometryPolygon, geometryMultipoint, geometryMultilinestring, geometryMultipolygon) {
        this.map = map;
        this.srid = 'EPSG:3857';
        this.mapSrid = 'EPSG:3857';
        if (!!geometryLinestring) {
            this.host = geometryLinestring;
        }
        else if (!!geometryPolygon) {
            this.host = geometryPolygon;
        }
        else if (!!geometryMultipoint) {
            this.host = geometryMultipoint;
        }
        else if (!!geometryMultilinestring) {
            this.host = geometryMultilinestring;
        }
        else if (!!geometryMultipolygon) {
            this.host = geometryMultipolygon;
        }
        else {
            throw new Error('aol-collection-coordinates must be a child of a geometry component');
        }
    }
    ngOnInit() {
        this.map.instance.on('change:view', (e) => this.onMapViewChanged(e));
        this.mapSrid = this.map.instance.getView().getProjection().getCode();
        this.transformCoordinates();
    }
    ngOnChanges(changes) {
        this.transformCoordinates();
    }
    onMapViewChanged(event) {
        this.mapSrid = event.target.get(event.key).getProjection().getCode();
        this.transformCoordinates();
    }
    transformCoordinates() {
        let transformedCoordinates;
        if (this.srid === this.mapSrid) {
            transformedCoordinates = this.coordinates;
        }
        else {
            switch (this.host.componentType) {
                case 'geometry-linestring':
                case 'geometry-multipoint':
                    transformedCoordinates = this.coordinates.map((c) => transform(c, this.srid, this.mapSrid));
                    break;
                case 'geometry-polygon':
                case 'geometry-multilinestring':
                    transformedCoordinates = this.coordinates.map((cc) => cc.map((c) => transform(c, this.srid, this.mapSrid)));
                    break;
                case 'geometry-multipolygon':
                    transformedCoordinates = this.coordinates.map((ccc) => ccc.map((cc) => cc.map((c) => transform(c, this.srid, this.mapSrid))));
                    break;
            }
        }
        this.host.instance.setCoordinates(transformedCoordinates);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: CollectionCoordinatesComponent, deps: [{ token: MapComponent }, { token: GeometryLinestringComponent, optional: true }, { token: GeometryPolygonComponent, optional: true }, { token: GeometryMultiPointComponent, optional: true }, { token: GeometryMultiLinestringComponent, optional: true }, { token: GeometryMultiPolygonComponent, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: CollectionCoordinatesComponent, isStandalone: true, selector: "aol-collection-coordinates", inputs: { coordinates: "coordinates", srid: "srid" }, usesOnChanges: true, ngImport: i0, template: ` <div class="aol-collection-coordinates"></div> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: CollectionCoordinatesComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-collection-coordinates',
                    template: ` <div class="aol-collection-coordinates"></div> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }, { type: GeometryLinestringComponent, decorators: [{
                    type: Optional
                }] }, { type: GeometryPolygonComponent, decorators: [{
                    type: Optional
                }] }, { type: GeometryMultiPointComponent, decorators: [{
                    type: Optional
                }] }, { type: GeometryMultiLinestringComponent, decorators: [{
                    type: Optional
                }] }, { type: GeometryMultiPolygonComponent, decorators: [{
                    type: Optional
                }] }], propDecorators: { coordinates: [{
                type: Input
            }], srid: [{
                type: Input
            }] } });

class StyleComponent {
    constructor(featureHost, layerHost) {
        this.componentType = 'style';
        // console.log('creating aol-style');
        this.host = featureHost ? featureHost : layerHost;
        if (!this.host) {
            throw new Error('aol-style must be applied to a feature or a layer');
        }
    }
    update() {
        // console.log('updating style\'s host: ', this.host);
        this.host.instance.changed();
    }
    ngOnInit() {
        // console.log('creating aol-style instance with: ', this);
        this.instance = new Style(this);
        this.host.instance.setStyle(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: StyleComponent, deps: [{ token: FeatureComponent, optional: true }, { token: LayerVectorComponent, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: StyleComponent, isStandalone: true, selector: "aol-style", inputs: { geometry: "geometry", fill: "fill", image: "image", stroke: "stroke", text: "text", zIndex: "zIndex" }, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: StyleComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-style',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: FeatureComponent, decorators: [{
                    type: Optional
                }] }, { type: LayerVectorComponent, decorators: [{
                    type: Optional
                }] }], propDecorators: { geometry: [{
                type: Input
            }], fill: [{
                type: Input
            }], image: [{
                type: Input
            }], stroke: [{
                type: Input
            }], text: [{
                type: Input
            }], zIndex: [{
                type: Input
            }] } });

class StyleCircleComponent {
    constructor(host) {
        this.host = host;
        this.componentType = 'style-circle';
    }
    /**
     * WORK-AROUND: since the re-rendering is not triggered on style change
     * we trigger a radius change.
     * see openlayers #6233 and #5775
     */
    update() {
        if (this.instance) {
            // console.log('setting ol.style.Circle instance\'s radius');
            this.instance.setRadius(this.radius);
        }
        this.host.update();
    }
    ngAfterContentInit() {
        // console.log('creating ol.style.Circle instance with: ', this);
        this.instance = new Circle$1(this);
        this.host.instance.setImage(this.instance);
        this.host.update();
    }
    ngOnChanges(changes) {
        if (!this.instance) {
            return;
        }
        if (changes.radius) {
            this.instance.setRadius(changes.radius.currentValue);
        }
        // console.log('changes detected in aol-style-circle, setting new radius: ', changes['radius'].currentValue);
    }
    ngOnDestroy() {
        // console.log('removing aol-style-circle');
        this.host.instance.setImage(null);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: StyleCircleComponent, deps: [{ token: StyleComponent, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: StyleCircleComponent, isStandalone: true, selector: "aol-style-circle", inputs: { fill: "fill", radius: "radius", snapToPixel: "snapToPixel", stroke: "stroke" }, usesOnChanges: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: StyleCircleComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-style-circle',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: StyleComponent, decorators: [{
                    type: Host
                }] }], propDecorators: { fill: [{
                type: Input
            }], radius: [{
                type: Input
            }], snapToPixel: [{
                type: Input
            }], stroke: [{
                type: Input
            }] } });

class StyleTextComponent {
    constructor(host) {
        this.host = host;
        this.componentType = 'style-text';
        if (!host) {
            throw new Error('aol-style-text must be a descendant of aol-style');
        }
        // console.log('creating aol-style-text with: ', this);
    }
    ngOnInit() {
        // console.log('creating ol.style.Text instance with: ', this);
        this.instance = new Text(this);
        this.host.instance.setText(this.instance);
    }
    ngOnChanges(changes) {
        if (!this.instance) {
            return;
        }
        if (changes.font) {
            this.instance.setFont(changes.font.currentValue);
        }
        if (changes.offsetX) {
            this.instance.setOffsetX(changes.offsetX.currentValue);
        }
        if (changes.offsetY) {
            this.instance.setOffsetY(changes.offsetY.currentValue);
        }
        if (changes.scale) {
            this.instance.setScale(changes.scale.currentValue);
        }
        if (changes.rotation) {
            this.instance.setRotation(changes.rotation.currentValue);
        }
        if (changes.text) {
            this.instance.setText(changes.text.currentValue);
        }
        if (changes.textAlign) {
            this.instance.setTextAlign(changes.textAlign.currentValue);
        }
        if (changes.textBaseLine) {
            this.instance.setTextBaseline(changes.textBaseLine.currentValue);
        }
        this.host.update();
        // console.log('changes detected in aol-style-text, setting new properties: ', changes);
    }
    update() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: StyleTextComponent, deps: [{ token: StyleComponent, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: StyleTextComponent, isStandalone: true, selector: "aol-style-text", inputs: { font: "font", offsetX: "offsetX", offsetY: "offsetY", scale: "scale", rotateWithView: "rotateWithView", rotation: "rotation", text: "text", textAlign: "textAlign", textBaseLine: "textBaseLine" }, usesOnChanges: true, ngImport: i0, template: ` <div class="aol-style-text"></div> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: StyleTextComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-style-text',
                    template: ` <div class="aol-style-text"></div> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: StyleComponent, decorators: [{
                    type: Optional
                }] }], propDecorators: { font: [{
                type: Input
            }], offsetX: [{
                type: Input
            }], offsetY: [{
                type: Input
            }], scale: [{
                type: Input
            }], rotateWithView: [{
                type: Input
            }], rotation: [{
                type: Input
            }], text: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textBaseLine: [{
                type: Input
            }] } });

class StyleStrokeComponent {
    constructor(styleHost, styleCircleHost, styleTextHost) {
        if (!styleHost) {
            throw new Error('aol-style-stroke must be a descendant of aol-style');
        }
        if (styleTextHost) {
            this.host = styleTextHost;
        }
        else if (styleCircleHost) {
            this.host = styleCircleHost;
        }
        else {
            this.host = styleHost;
        }
        // console.log('creating aol-style-stroke with: ', this);
    }
    ngOnInit() {
        // console.log('creating ol.style.Stroke instance with: ', this);
        this.instance = new Stroke(this);
        switch (this.host.componentType) {
            case 'style':
                this.host.instance.setStroke(this.instance);
                // console.log('setting ol.style instance\'s stroke:', this.host);
                break;
            case 'style-text':
                this.host.instance.setStroke(this.instance);
                break;
            case 'style-circle':
                this.host.stroke = this.instance;
                // console.log('setting ol.style.circle instance\'s stroke:', this.host);
                break;
            default:
                throw new Error('unknown host type: ' + this.host);
            // break;
        }
    }
    ngOnChanges(changes) {
        if (!this.instance) {
            return;
        }
        if (changes.color) {
            this.instance.setColor(changes.color.currentValue);
        }
        if (changes.lineCap) {
            this.instance.setLineCap(changes.lineCap.currentValue);
        }
        if (changes.lineDash) {
            this.instance.setLineDash(changes.lineDash.currentValue);
        }
        if (changes.lineJoin) {
            this.instance.setLineJoin(changes.lineJoin.currentValue);
        }
        if (changes.miterLimit) {
            this.instance.setMiterLimit(changes.miterLimit.currentValue);
        }
        if (changes.width) {
            this.instance.setWidth(changes.width.currentValue);
        }
        this.host.update();
        // console.log('changes detected in aol-style-stroke, setting new properties: ', changes);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: StyleStrokeComponent, deps: [{ token: StyleComponent, optional: true }, { token: StyleCircleComponent, optional: true }, { token: StyleTextComponent, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: StyleStrokeComponent, isStandalone: true, selector: "aol-style-stroke", inputs: { color: "color", lineCap: "lineCap", lineDash: "lineDash", lineJoin: "lineJoin", miterLimit: "miterLimit", width: "width" }, usesOnChanges: true, ngImport: i0, template: ` <div class="aol-style-stroke"></div> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: StyleStrokeComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-style-stroke',
                    template: ` <div class="aol-style-stroke"></div> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: StyleComponent, decorators: [{
                    type: Optional
                }] }, { type: StyleCircleComponent, decorators: [{
                    type: Optional
                }] }, { type: StyleTextComponent, decorators: [{
                    type: Optional
                }] }], propDecorators: { color: [{
                type: Input
            }], lineCap: [{
                type: Input
            }], lineDash: [{
                type: Input
            }], lineJoin: [{
                type: Input
            }], miterLimit: [{
                type: Input
            }], width: [{
                type: Input
            }] } });

class StyleIconComponent {
    constructor(host) {
        this.host = host;
    }
    ngOnInit() {
        // console.log('creating ol.style.Icon instance with: ', this);
        this.instance = new Icon(this);
        this.host.instance.setImage(this.instance);
    }
    ngOnChanges(changes) {
        if (!this.instance) {
            return;
        }
        if (changes.opacity) {
            this.instance.setOpacity(changes.opacity.currentValue);
        }
        if (changes.rotation) {
            this.instance.setRotation(changes.rotation.currentValue);
        }
        if (changes.scale) {
            this.instance.setScale(changes.scale.currentValue);
        }
        if (changes.src) {
            this.instance = new Icon(this);
            this.host.instance.setImage(this.instance);
        }
        this.host.update();
        // console.log('changes detected in aol-style-icon: ', changes);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: StyleIconComponent, deps: [{ token: StyleComponent, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: StyleIconComponent, isStandalone: true, selector: "aol-style-icon", inputs: { anchor: "anchor", anchorXUnits: "anchorXUnits", anchorYUnits: "anchorYUnits", anchorOrigin: "anchorOrigin", color: "color", crossOrigin: "crossOrigin", img: "img", offset: "offset", offsetOrigin: "offsetOrigin", opacity: "opacity", scale: "scale", snapToPixel: "snapToPixel", rotateWithView: "rotateWithView", rotation: "rotation", size: "size", imgSize: "imgSize", src: "src" }, usesOnChanges: true, ngImport: i0, template: ` <div class="aol-style-icon"></div> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: StyleIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-style-icon',
                    template: ` <div class="aol-style-icon"></div> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: StyleComponent, decorators: [{
                    type: Host
                }] }], propDecorators: { anchor: [{
                type: Input
            }], anchorXUnits: [{
                type: Input
            }], anchorYUnits: [{
                type: Input
            }], anchorOrigin: [{
                type: Input
            }], color: [{
                type: Input
            }], crossOrigin: [{
                type: Input
            }], img: [{
                type: Input
            }], offset: [{
                type: Input
            }], offsetOrigin: [{
                type: Input
            }], opacity: [{
                type: Input
            }], scale: [{
                type: Input
            }], snapToPixel: [{
                type: Input
            }], rotateWithView: [{
                type: Input
            }], rotation: [{
                type: Input
            }], size: [{
                type: Input
            }], imgSize: [{
                type: Input
            }], src: [{
                type: Input
            }] } });

class StyleFillComponent {
    constructor(styleHost, styleCircleHost, styleTextHost) {
        if (!styleHost) {
            throw new Error('aol-style-stroke must be a descendant of aol-style');
        }
        if (styleTextHost) {
            this.host = styleTextHost;
        }
        else if (styleCircleHost) {
            this.host = styleCircleHost;
        }
        else {
            this.host = styleHost;
        }
        // console.log('creating aol-style-fill with: ', this);
    }
    ngOnInit() {
        // console.log('creating ol.style.Fill instance with: ', this);
        this.instance = new Fill(this);
        switch (this.host.componentType) {
            case 'style':
                this.host.instance.setFill(this.instance);
                // console.log('setting ol.style instance\'s fill:', this.host);
                break;
            case 'style-text':
                this.host.instance.setFill(this.instance);
                break;
            case 'style-circle':
                this.host.fill = this.instance;
                // console.log('setting ol.style.circle instance\'s fill:', this.host);
                break;
            default:
                throw new Error('unknown host type: ' + this.host);
        }
    }
    ngOnChanges(changes) {
        if (!this.instance) {
            return;
        }
        if (changes.color) {
            this.instance.setColor(changes.color.currentValue);
        }
        this.host.update();
        // console.log('changes detected in aol-style-fill, setting new color: ', changes);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: StyleFillComponent, deps: [{ token: StyleComponent, optional: true }, { token: StyleCircleComponent, optional: true }, { token: StyleTextComponent, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: StyleFillComponent, isStandalone: true, selector: "aol-style-fill", inputs: { color: "color" }, usesOnChanges: true, ngImport: i0, template: ` <div class="aol-style-fill"></div> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: StyleFillComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-style-fill',
                    template: ` <div class="aol-style-fill"></div> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: StyleComponent, decorators: [{
                    type: Optional
                }] }, { type: StyleCircleComponent, decorators: [{
                    type: Optional
                }] }, { type: StyleTextComponent, decorators: [{
                    type: Optional
                }] }], propDecorators: { color: [{
                type: Input
            }] } });

class DefaultControlComponent {
    constructor(map) {
        this.map = map;
    }
    ngOnInit() {
        // console.log('ol.control.defaults init: ', this);
        this.instance = defaults(this);
        this.instance.forEach((c) => this.map.instance.addControl(c));
    }
    ngOnDestroy() {
        // console.log('removing aol-control-defaults');
        this.instance.forEach((c) => this.map.instance.removeControl(c));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DefaultControlComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: DefaultControlComponent, isStandalone: true, selector: "aol-control-defaults", inputs: { attribution: "attribution", attributionOptions: "attributionOptions", rotate: "rotate", rotateOptions: "rotateOptions", zoom: "zoom", zoomOptions: "zoomOptions" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DefaultControlComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-control-defaults',
                    template: '',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { attribution: [{
                type: Input
            }], attributionOptions: [{
                type: Input
            }], rotate: [{
                type: Input
            }], rotateOptions: [{
                type: Input
            }], zoom: [{
                type: Input
            }], zoomOptions: [{
                type: Input
            }] } });

class ControlComponent {
    constructor(map) {
        this.map = map;
        this.componentType = 'control';
    }
    ngOnInit() {
        if (this.content) {
            this.element = this.content.elementRef.nativeElement;
            this.instance = new Control(this);
            this.map.instance.addControl(this.instance);
        }
    }
    ngOnDestroy() {
        if (this.instance) {
            this.map.instance.removeControl(this.instance);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ControlComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: ControlComponent, isStandalone: true, selector: "aol-control", queries: [{ propertyName: "content", first: true, predicate: ContentComponent, descendants: true, static: true }], ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ControlComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-control',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { content: [{
                type: ContentChild,
                args: [ContentComponent, { static: true }]
            }] } });

class ControlAttributionComponent {
    constructor(map, element) {
        this.map = map;
        this.element = element;
        this.componentType = 'control';
    }
    ngOnInit() {
        this.target = this.element.nativeElement;
        // console.log('ol.control.Attribution init: ', this);
        this.instance = new Attribution(this);
        this.map.instance.addControl(this.instance);
    }
    ngOnDestroy() {
        // console.log('removing aol-control-attribution');
        this.map.instance.removeControl(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ControlAttributionComponent, deps: [{ token: MapComponent }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: ControlAttributionComponent, isStandalone: true, selector: "aol-control-attribution", inputs: { collapsible: "collapsible" }, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ControlAttributionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-control-attribution',
                    template: ``,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }, { type: i0.ElementRef }], propDecorators: { collapsible: [{
                type: Input
            }] } });

class ControlFullScreenComponent {
    constructor(map) {
        this.map = map;
        // console.log('instancing aol-control-fullscreen');
    }
    ngOnInit() {
        this.instance = new FullScreen(this);
        this.map.instance.addControl(this.instance);
    }
    ngOnDestroy() {
        // console.log('removing aol-control-fullscreen');
        this.map.instance.removeControl(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ControlFullScreenComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: ControlFullScreenComponent, isStandalone: true, selector: "aol-control-fullscreen", inputs: { className: "className", label: "label", labelActive: "labelActive", tipLabel: "tipLabel", keys: "keys" }, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ControlFullScreenComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-control-fullscreen',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { className: [{
                type: Input
            }], label: [{
                type: Input
            }], labelActive: [{
                type: Input
            }], tipLabel: [{
                type: Input
            }], keys: [{
                type: Input
            }] } });

class ControlMousePositionComponent {
    constructor(map, element) {
        this.map = map;
        this.element = element;
    }
    ngOnInit() {
        this.target = this.element.nativeElement;
        // console.log('ol.control.MousePosition init: ', this);
        this.instance = new MousePosition(this);
        this.map.instance.addControl(this.instance);
    }
    ngOnDestroy() {
        // console.log('removing aol-control-mouseposition');
        this.map.instance.removeControl(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ControlMousePositionComponent, deps: [{ token: MapComponent }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: ControlMousePositionComponent, isStandalone: true, selector: "aol-control-mouseposition", inputs: { coordinateFormat: "coordinateFormat", projection: "projection" }, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ControlMousePositionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-control-mouseposition',
                    template: ``,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }, { type: i0.ElementRef }], propDecorators: { coordinateFormat: [{
                type: Input
            }], projection: [{
                type: Input
            }] } });

class ControlOverviewMapComponent {
    constructor(map) {
        this.map = map;
    }
    ngOnInit() {
        this.instance = new OverviewMap(this);
        this.map.instance.addControl(this.instance);
    }
    ngOnDestroy() {
        this.map.instance.removeControl(this.instance);
    }
    ngOnChanges(changes) {
        if (this.instance != null && changes.hasOwnProperty('view')) {
            this.reloadInstance();
        }
    }
    reloadInstance() {
        this.map.instance.removeControl(this.instance);
        this.instance = new OverviewMap(this);
        this.map.instance.addControl(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ControlOverviewMapComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: ControlOverviewMapComponent, isStandalone: true, selector: "aol-control-overviewmap", inputs: { collapsed: "collapsed", collapseLabel: "collapseLabel", collapsible: "collapsible", label: "label", layers: "layers", target: "target", tipLabel: "tipLabel", view: "view" }, usesOnChanges: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ControlOverviewMapComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-control-overviewmap',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { collapsed: [{
                type: Input
            }], collapseLabel: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], label: [{
                type: Input
            }], layers: [{
                type: Input
            }], target: [{
                type: Input
            }], tipLabel: [{
                type: Input
            }], view: [{
                type: Input
            }] } });

class ControlRotateComponent {
    constructor(map) {
        this.map = map;
        // console.log('instancing aol-control-rotate');
    }
    ngOnInit() {
        this.instance = new Rotate(this);
        this.map.instance.addControl(this.instance);
    }
    ngOnDestroy() {
        // console.log('removing aol-control-rotate');
        this.map.instance.removeControl(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ControlRotateComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: ControlRotateComponent, isStandalone: true, selector: "aol-control-rotate", inputs: { className: "className", label: "label", tipLabel: "tipLabel", duration: "duration", autoHide: "autoHide" }, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ControlRotateComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-control-rotate',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { className: [{
                type: Input
            }], label: [{
                type: Input
            }], tipLabel: [{
                type: Input
            }], duration: [{
                type: Input
            }], autoHide: [{
                type: Input
            }] } });

class ControlScaleLineComponent {
    constructor(map) {
        this.map = map;
    }
    ngOnInit() {
        this.instance = new ScaleLine(this);
        this.map.instance.addControl(this.instance);
    }
    ngOnDestroy() {
        this.map.instance.removeControl(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ControlScaleLineComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: ControlScaleLineComponent, isStandalone: true, selector: "aol-control-scaleline", inputs: { units: "units" }, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ControlScaleLineComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-control-scaleline',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { units: [{
                type: Input
            }] } });

class ControlZoomComponent {
    constructor(map) {
        this.map = map;
        // console.log('instancing aol-control-zoom');
    }
    ngOnInit() {
        this.instance = new Zoom(this);
        this.map.instance.addControl(this.instance);
    }
    ngOnDestroy() {
        // console.log('removing aol-control-zoom');
        this.map.instance.removeControl(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ControlZoomComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: ControlZoomComponent, isStandalone: true, selector: "aol-control-zoom", inputs: { duration: "duration", zoomInLabel: "zoomInLabel", zoomOutLabel: "zoomOutLabel", zoomInTipLabel: "zoomInTipLabel", zoomOutTipLabel: "zoomOutTipLabel", delta: "delta" }, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ControlZoomComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-control-zoom',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { duration: [{
                type: Input
            }], zoomInLabel: [{
                type: Input
            }], zoomOutLabel: [{
                type: Input
            }], zoomInTipLabel: [{
                type: Input
            }], zoomOutTipLabel: [{
                type: Input
            }], delta: [{
                type: Input
            }] } });

class ControlZoomSliderComponent {
    constructor(map) {
        this.map = map;
        // console.log('instancing aol-control-zoomslider');
    }
    ngOnInit() {
        this.instance = new ZoomSlider(this);
        this.map.instance.addControl(this.instance);
    }
    ngOnDestroy() {
        // console.log('removing aol-control-zoomslider');
        this.map.instance.removeControl(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ControlZoomSliderComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: ControlZoomSliderComponent, isStandalone: true, selector: "aol-control-zoomslider", inputs: { className: "className", duration: "duration", maxResolution: "maxResolution", minResolution: "minResolution" }, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ControlZoomSliderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-control-zoomslider',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { className: [{
                type: Input
            }], duration: [{
                type: Input
            }], maxResolution: [{
                type: Input
            }], minResolution: [{
                type: Input
            }] } });

class ControlZoomToExtentComponent {
    constructor(map) {
        this.map = map;
        // console.log('instancing aol-control-zoomtoextent');
    }
    ngOnInit() {
        this.instance = new ZoomToExtent(this);
        this.map.instance.addControl(this.instance);
    }
    ngOnDestroy() {
        // console.log('removing aol-control-zoomtoextent');
        this.map.instance.removeControl(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ControlZoomToExtentComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: ControlZoomToExtentComponent, isStandalone: true, selector: "aol-control-zoomtoextent", inputs: { className: "className", label: "label", tipLabel: "tipLabel", extent: "extent" }, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ControlZoomToExtentComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-control-zoomtoextent',
                    template: ` <ng-content></ng-content> `,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { className: [{
                type: Input
            }], label: [{
                type: Input
            }], tipLabel: [{
                type: Input
            }], extent: [{
                type: Input
            }] } });

class FormatMVTComponent extends FormatComponent {
    constructor() {
        super();
        this.instance = new MVT(this);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: FormatMVTComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: FormatMVTComponent, isStandalone: true, selector: "aol-format-mvt", inputs: { featureClass: "featureClass", geometryName: "geometryName", layerName: "layerName", layers: "layers" }, providers: [{ provide: FormatComponent, useExisting: forwardRef(() => FormatMVTComponent) }], usesInheritance: true, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: FormatMVTComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-format-mvt',
                    template: '',
                    providers: [{ provide: FormatComponent, useExisting: forwardRef(() => FormatMVTComponent) }],
                    standalone: true,
                }]
        }], ctorParameters: () => [], propDecorators: { featureClass: [{
                type: Input
            }], geometryName: [{
                type: Input
            }], layerName: [{
                type: Input
            }], layers: [{
                type: Input
            }] } });

class DefaultInteractionComponent {
    constructor(map) {
        this.map = map;
    }
    ngOnInit() {
        this.instance = defaults$1(this);
        this.instance.forEach((i) => this.map.instance.addInteraction(i));
    }
    ngOnDestroy() {
        this.instance.forEach((i) => this.map.instance.removeInteraction(i));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DefaultInteractionComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: DefaultInteractionComponent, isStandalone: true, selector: "aol-interaction-default", inputs: { altShiftDragRotate: "altShiftDragRotate", onFocusOnly: "onFocusOnly", doubleClickZoom: "doubleClickZoom", keyboard: "keyboard", mouseWheelZoom: "mouseWheelZoom", shiftDragZoom: "shiftDragZoom", dragPan: "dragPan", pinchRotate: "pinchRotate", pinchZoom: "pinchZoom", zoomDelta: "zoomDelta", zoomDuration: "zoomDuration" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DefaultInteractionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-interaction-default',
                    template: '',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { altShiftDragRotate: [{
                type: Input
            }], onFocusOnly: [{
                type: Input
            }], doubleClickZoom: [{
                type: Input
            }], keyboard: [{
                type: Input
            }], mouseWheelZoom: [{
                type: Input
            }], shiftDragZoom: [{
                type: Input
            }], dragPan: [{
                type: Input
            }], pinchRotate: [{
                type: Input
            }], pinchZoom: [{
                type: Input
            }], zoomDelta: [{
                type: Input
            }], zoomDuration: [{
                type: Input
            }] } });

class DoubleClickZoomInteractionComponent {
    constructor(map) {
        this.map = map;
    }
    ngOnInit() {
        this.instance = new DoubleClickZoom(this);
        this.map.instance.addInteraction(this.instance);
    }
    ngOnDestroy() {
        this.map.instance.removeInteraction(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DoubleClickZoomInteractionComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: DoubleClickZoomInteractionComponent, isStandalone: true, selector: "aol-interaction-doubleclickzoom", inputs: { duration: "duration", delta: "delta" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DoubleClickZoomInteractionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-interaction-doubleclickzoom',
                    template: '',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { duration: [{
                type: Input
            }], delta: [{
                type: Input
            }] } });

class DragAndDropInteractionComponent {
    constructor(map) {
        this.map = map;
    }
    ngOnInit() {
        this.instance = new DragAndDrop(this);
        this.map.instance.addInteraction(this.instance);
    }
    ngOnDestroy() {
        this.map.instance.removeInteraction(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DragAndDropInteractionComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: DragAndDropInteractionComponent, isStandalone: true, selector: "aol-interaction-draganddrop", inputs: { formatConstructors: "formatConstructors", projection: "projection", target: "target" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DragAndDropInteractionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-interaction-draganddrop',
                    template: '',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { formatConstructors: [{
                type: Input
            }], projection: [{
                type: Input
            }], target: [{
                type: Input
            }] } });

class DragBoxInteractionComponent {
    constructor(map) {
        this.map = map;
    }
    ngOnInit() {
        this.instance = new DragBox(this);
        this.map.instance.addInteraction(this.instance);
    }
    ngOnDestroy() {
        this.map.instance.removeInteraction(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DragBoxInteractionComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: DragBoxInteractionComponent, isStandalone: true, selector: "aol-interaction-dragbox", inputs: { className: "className", condition: "condition", boxEndCondition: "boxEndCondition" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DragBoxInteractionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-interaction-dragbox',
                    template: '',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { className: [{
                type: Input
            }], condition: [{
                type: Input
            }], boxEndCondition: [{
                type: Input
            }] } });

class DragPanInteractionComponent {
    constructor(map) {
        this.map = map;
    }
    ngOnInit() {
        this.instance = new DragPan(this);
        this.map.instance.addInteraction(this.instance);
    }
    ngOnDestroy() {
        this.map.instance.removeInteraction(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DragPanInteractionComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: DragPanInteractionComponent, isStandalone: true, selector: "aol-interaction-dragpan", inputs: { condition: "condition", kinetic: "kinetic" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DragPanInteractionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-interaction-dragpan',
                    template: '',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { condition: [{
                type: Input
            }], kinetic: [{
                type: Input
            }] } });

class DragRotateInteractionComponent {
    constructor(map) {
        this.map = map;
    }
    ngOnInit() {
        this.instance = new DragRotate(this);
        this.map.instance.addInteraction(this.instance);
    }
    ngOnDestroy() {
        this.map.instance.removeInteraction(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DragRotateInteractionComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: DragRotateInteractionComponent, isStandalone: true, selector: "aol-interaction-dragrotate", inputs: { condition: "condition", duration: "duration" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DragRotateInteractionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-interaction-dragrotate',
                    template: '',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { condition: [{
                type: Input
            }], duration: [{
                type: Input
            }] } });

class DragRotateAndZoomInteractionComponent {
    constructor(map) {
        this.map = map;
    }
    ngOnInit() {
        this.instance = new DragRotateAndZoom(this);
        this.map.instance.addInteraction(this.instance);
    }
    ngOnDestroy() {
        this.map.instance.removeInteraction(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DragRotateAndZoomInteractionComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: DragRotateAndZoomInteractionComponent, isStandalone: true, selector: "aol-interaction-dragrotateandzoom", inputs: { condition: "condition", duration: "duration" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DragRotateAndZoomInteractionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-interaction-dragrotateandzoom',
                    template: '',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { condition: [{
                type: Input
            }], duration: [{
                type: Input
            }] } });

class DragZoomInteractionComponent {
    constructor(map) {
        this.map = map;
    }
    ngOnInit() {
        this.instance = new DragZoom(this);
        this.map.instance.addInteraction(this.instance);
    }
    ngOnDestroy() {
        this.map.instance.removeInteraction(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DragZoomInteractionComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: DragZoomInteractionComponent, isStandalone: true, selector: "aol-interaction-dragzoom", inputs: { className: "className", condition: "condition", duration: "duration", out: "out" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DragZoomInteractionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-interaction-dragzoom',
                    template: '',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { className: [{
                type: Input
            }], condition: [{
                type: Input
            }], duration: [{
                type: Input
            }], out: [{
                type: Input
            }] } });

class MouseWheelZoomInteractionComponent {
    constructor(map) {
        this.map = map;
    }
    ngOnInit() {
        this.instance = new MouseWheelZoom(this);
        this.map.instance.addInteraction(this.instance);
    }
    ngOnDestroy() {
        this.map.instance.removeInteraction(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: MouseWheelZoomInteractionComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: MouseWheelZoomInteractionComponent, isStandalone: true, selector: "aol-interaction-mousewheelzoom", inputs: { duration: "duration", timeout: "timeout", useAnchor: "useAnchor" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: MouseWheelZoomInteractionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-interaction-mousewheelzoom',
                    template: '',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { duration: [{
                type: Input
            }], timeout: [{
                type: Input
            }], useAnchor: [{
                type: Input
            }] } });

class PinchZoomInteractionComponent {
    constructor(map) {
        this.map = map;
    }
    ngOnInit() {
        this.instance = new PinchZoom(this);
        this.map.instance.addInteraction(this.instance);
    }
    ngOnDestroy() {
        this.map.instance.removeInteraction(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: PinchZoomInteractionComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: PinchZoomInteractionComponent, isStandalone: true, selector: "aol-interaction-pinchzoom", inputs: { duration: "duration", constrainResolution: "constrainResolution" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: PinchZoomInteractionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-interaction-pinchzoom',
                    template: '',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { duration: [{
                type: Input
            }], constrainResolution: [{
                type: Input
            }] } });

class DrawInteractionComponent {
    constructor(map) {
        this.map = map;
        this.olChange = new EventEmitter();
        this.olChangeActive = new EventEmitter();
        this.olDrawAbort = new EventEmitter();
        this.drawEnd = new EventEmitter();
        this.drawStart = new EventEmitter();
        this.olError = new EventEmitter();
        this.propertyChange = new EventEmitter();
    }
    ngOnInit() {
        this.instance = new Draw(this);
        this.instance.on('change', (event) => this.olChange.emit(event));
        this.instance.on('change:active', (event) => this.olChangeActive.emit(event));
        this.instance.on('drawabort', (event) => this.olDrawAbort.emit(event));
        this.instance.on('drawend', (event) => this.drawEnd.emit(event));
        this.instance.on('drawstart', (event) => this.drawStart.emit(event));
        this.instance.on('error', (event) => this.olError.emit(event));
        this.instance.on('propertychange', (event) => this.propertyChange.emit(event));
        this.map.instance.addInteraction(this.instance);
    }
    handleKeydown() {
        this.instance.removeLastPoint();
    }
    ngOnDestroy() {
        this.map.instance.removeInteraction(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DrawInteractionComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: DrawInteractionComponent, isStandalone: true, selector: "aol-interaction-draw", inputs: { clickTolerance: "clickTolerance", features: "features", source: "source", snapTolerance: "snapTolerance", type: "type", maxPoints: "maxPoints", minPoints: "minPoints", finishCondition: "finishCondition", style: "style", geometryFunction: "geometryFunction", geometryName: "geometryName", condition: "condition", freehandCondition: "freehandCondition", freehand: "freehand", wrapX: "wrapX" }, outputs: { olChange: "olChange", olChangeActive: "olChangeActive", olDrawAbort: "olDrawAbort", drawEnd: "drawEnd", drawStart: "drawStart", olError: "olError", propertyChange: "propertyChange" }, host: { listeners: { "window:keydown.control.z": "handleKeydown($event)" } }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DrawInteractionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-interaction-draw',
                    template: '',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { clickTolerance: [{
                type: Input
            }], features: [{
                type: Input
            }], source: [{
                type: Input
            }], snapTolerance: [{
                type: Input
            }], type: [{
                type: Input
            }], maxPoints: [{
                type: Input
            }], minPoints: [{
                type: Input
            }], finishCondition: [{
                type: Input
            }], style: [{
                type: Input
            }], geometryFunction: [{
                type: Input
            }], geometryName: [{
                type: Input
            }], condition: [{
                type: Input
            }], freehandCondition: [{
                type: Input
            }], freehand: [{
                type: Input
            }], wrapX: [{
                type: Input
            }], olChange: [{
                type: Output
            }], olChangeActive: [{
                type: Output
            }], olDrawAbort: [{
                type: Output
            }], drawEnd: [{
                type: Output
            }], drawStart: [{
                type: Output
            }], olError: [{
                type: Output
            }], propertyChange: [{
                type: Output
            }], handleKeydown: [{
                type: HostListener,
                args: ['window:keydown.control.z', ['$event']]
            }] } });

class KeyboardPanInteractionComponent {
    constructor(map) {
        this.map = map;
    }
    ngOnInit() {
        this.instance = new KeyboardPan(this);
        this.map.instance.addInteraction(this.instance);
    }
    ngOnDestroy() {
        this.map.instance.removeInteraction(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: KeyboardPanInteractionComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: KeyboardPanInteractionComponent, isStandalone: false, selector: "aol-interaction-keyboardpan", inputs: { duration: "duration", pixelDelta: "pixelDelta" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: KeyboardPanInteractionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-interaction-keyboardpan',
                    template: '',
                    standalone: false
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { duration: [{
                type: Input
            }], pixelDelta: [{
                type: Input
            }] } });

class KeyboardZoomInteractionComponent {
    constructor(map) {
        this.map = map;
    }
    ngOnInit() {
        this.instance = new KeyboardZoom(this);
        this.map.instance.addInteraction(this.instance);
    }
    ngOnDestroy() {
        this.map.instance.removeInteraction(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: KeyboardZoomInteractionComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: KeyboardZoomInteractionComponent, isStandalone: false, selector: "aol-interaction-keyboardpan", inputs: { duration: "duration", delta: "delta" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: KeyboardZoomInteractionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-interaction-keyboardpan',
                    template: '',
                    standalone: false,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { duration: [{
                type: Input
            }], delta: [{
                type: Input
            }] } });

class SelectInteractionComponent {
    constructor(map) {
        this.map = map;
        this.olChange = new EventEmitter();
        this.olChangeActive = new EventEmitter();
        this.olError = new EventEmitter();
        this.propertyChange = new EventEmitter();
        this.olSelect = new EventEmitter();
    }
    ngOnInit() {
        this.instance = new Select(this);
        this.instance.on('change', (event) => this.olChange.emit(event));
        this.instance.on('change:active', (event) => this.olChangeActive.emit(event));
        this.instance.on('error', (event) => this.olError.emit(event));
        this.instance.on('propertychange', (event) => this.propertyChange.emit(event));
        this.instance.on('select', (event) => this.olSelect.emit(event));
        this.map.instance.addInteraction(this.instance);
    }
    ngOnDestroy() {
        this.map.instance.removeInteraction(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SelectInteractionComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: SelectInteractionComponent, isStandalone: true, selector: "aol-interaction-select", inputs: { addCondition: "addCondition", condition: "condition", layers: "layers", style: "style", removeCondition: "removeCondition", toggleCondition: "toggleCondition", multi: "multi", features: "features", filter: "filter", wrapX: "wrapX" }, outputs: { olChange: "olChange", olChangeActive: "olChangeActive", olError: "olError", propertyChange: "propertyChange", olSelect: "olSelect" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SelectInteractionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-interaction-select',
                    template: '',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { addCondition: [{
                type: Input
            }], condition: [{
                type: Input
            }], layers: [{
                type: Input
            }], style: [{
                type: Input
            }], removeCondition: [{
                type: Input
            }], toggleCondition: [{
                type: Input
            }], multi: [{
                type: Input
            }], features: [{
                type: Input
            }], filter: [{
                type: Input
            }], wrapX: [{
                type: Input
            }], olChange: [{
                type: Output
            }], olChangeActive: [{
                type: Output
            }], olError: [{
                type: Output
            }], propertyChange: [{
                type: Output
            }], olSelect: [{
                type: Output
            }] } });

class ModifyInteractionComponent {
    constructor(map) {
        this.map = map;
        this.olChange = new EventEmitter();
        this.olChangeActive = new EventEmitter();
        this.olError = new EventEmitter();
        this.olModifyEnd = new EventEmitter();
        this.olModifyStart = new EventEmitter();
        this.propertyChange = new EventEmitter();
    }
    ngOnInit() {
        this.instance = new Modify(this);
        this.instance.on('change', (event) => this.olChange.emit(event));
        this.instance.on('change:active', (event) => this.olChangeActive.emit(event));
        this.instance.on('error', (event) => this.olError.emit(event));
        this.instance.on('modifyend', (event) => this.olModifyEnd.emit(event));
        this.instance.on('modifystart', (event) => this.olModifyStart.emit(event));
        this.instance.on('propertychange', (event) => this.propertyChange.emit(event));
        this.map.instance.addInteraction(this.instance);
    }
    ngOnDestroy() {
        this.map.instance.removeInteraction(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ModifyInteractionComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: ModifyInteractionComponent, isStandalone: true, selector: "aol-interaction-modify", inputs: { condition: "condition", deleteCondition: "deleteCondition", pixelTolerance: "pixelTolerance", style: "style", features: "features", wrapX: "wrapX", source: "source" }, outputs: { olChange: "olChange", olChangeActive: "olChangeActive", olError: "olError", olModifyEnd: "olModifyEnd", olModifyStart: "olModifyStart", propertyChange: "propertyChange" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: ModifyInteractionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-interaction-modify',
                    template: '',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { condition: [{
                type: Input
            }], deleteCondition: [{
                type: Input
            }], pixelTolerance: [{
                type: Input
            }], style: [{
                type: Input
            }], features: [{
                type: Input
            }], wrapX: [{
                type: Input
            }], source: [{
                type: Input
            }], olChange: [{
                type: Output
            }], olChangeActive: [{
                type: Output
            }], olError: [{
                type: Output
            }], olModifyEnd: [{
                type: Output
            }], olModifyStart: [{
                type: Output
            }], propertyChange: [{
                type: Output
            }] } });

class TranslateInteractionComponent {
    constructor(map) {
        this.map = map;
        this.olChange = new EventEmitter();
        this.olChangeActive = new EventEmitter();
        this.olError = new EventEmitter();
        this.propertyChange = new EventEmitter();
        this.translateEnd = new EventEmitter();
        this.translateStart = new EventEmitter();
        this.translating = new EventEmitter();
    }
    ngOnInit() {
        this.instance = new Translate(this);
        this.instance.on('change', (event) => this.olChange.emit(event));
        this.instance.on('change:active', (event) => this.olChangeActive.emit(event));
        this.instance.on('error', (event) => this.olError.emit(event));
        this.instance.on('propertychange', (event) => this.propertyChange.emit(event));
        this.instance.on('translateend', (event) => this.translateEnd.emit(event));
        this.instance.on('translatestart', (event) => this.translateStart.emit(event));
        this.instance.on('translating', (event) => this.translating.emit(event));
        this.map.instance.addInteraction(this.instance);
    }
    ngOnDestroy() {
        this.map.instance.removeInteraction(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: TranslateInteractionComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: TranslateInteractionComponent, isStandalone: true, selector: "aol-interaction-translate", inputs: { features: "features", layers: "layers", hitTolerance: "hitTolerance" }, outputs: { olChange: "olChange", olChangeActive: "olChangeActive", olError: "olError", propertyChange: "propertyChange", translateEnd: "translateEnd", translateStart: "translateStart", translating: "translating" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: TranslateInteractionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-interaction-translate',
                    template: '',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { features: [{
                type: Input
            }], layers: [{
                type: Input
            }], hitTolerance: [{
                type: Input
            }], olChange: [{
                type: Output
            }], olChangeActive: [{
                type: Output
            }], olError: [{
                type: Output
            }], propertyChange: [{
                type: Output
            }], translateEnd: [{
                type: Output
            }], translateStart: [{
                type: Output
            }], translating: [{
                type: Output
            }] } });

class AttributionComponent {
    constructor(elementRef) {
        this.elementRef = elementRef;
    }
    ngOnInit() {
        this.label = this.elementRef.nativeElement.innerHTML;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: AttributionComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: AttributionComponent, isStandalone: true, selector: "aol-attribution", ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: AttributionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-attribution',
                    template: '<ng-content></ng-content>',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }] });

class AttributionsComponent {
    constructor(source) {
        this.source = source;
    }
    /* we can do this at the very end */
    ngAfterViewInit() {
        if (this.attributions.length) {
            this.instance = this.attributions.map((cmp) => cmp.label);
            // console.log('setting attributions:', this.instance);
            this.source.instance.setAttributions(this.instance);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: AttributionsComponent, deps: [{ token: SourceComponent, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: AttributionsComponent, isStandalone: true, selector: "aol-attributions", queries: [{ propertyName: "attributions", predicate: AttributionComponent }], ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: AttributionsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-attributions',
                    template: '<ng-content></ng-content>',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: SourceComponent, decorators: [{
                    type: Host
                }] }], propDecorators: { attributions: [{
                type: ContentChildren,
                args: [AttributionComponent]
            }] } });

class SourceUTFGridComponent extends SourceComponent {
    constructor(layer) {
        super(layer);
    }
    ngOnInit() {
        this.instance = new UTFGrid(this);
        this.host.instance.setSource(this.instance);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceUTFGridComponent, deps: [{ token: LayerTileComponent, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: SourceUTFGridComponent, isStandalone: true, selector: "aol-source-utfgrid", inputs: { tileJSON: "tileJSON", url: "url" }, providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceUTFGridComponent) }], usesInheritance: true, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: SourceUTFGridComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-source-utfgrid',
                    template: ` <ng-content></ng-content> `,
                    providers: [{ provide: SourceComponent, useExisting: forwardRef(() => SourceUTFGridComponent) }],
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: LayerTileComponent, decorators: [{
                    type: Host
                }] }], propDecorators: { tileJSON: [{
                type: Input
            }], url: [{
                type: Input
            }] } });

var DrawHoleInPolygonInteractionErrorType;
(function (DrawHoleInPolygonInteractionErrorType) {
    DrawHoleInPolygonInteractionErrorType["MoPolygonFound"] = "noPolygonFound";
    DrawHoleInPolygonInteractionErrorType["DrawVertexOutsidePolygon"] = "drawVertexOutsidePolygon";
    DrawHoleInPolygonInteractionErrorType["NoLinearRingFoundToRemove"] = "noLinearRingFoundToRemove";
    DrawHoleInPolygonInteractionErrorType["NoVectorLayerFound"] = "noVectorLayerFound";
})(DrawHoleInPolygonInteractionErrorType || (DrawHoleInPolygonInteractionErrorType = {}));
class DrawHoleInPolygonInteractionComponent {
    constructor(map) {
        this.map = map;
        this.drawEnd = new EventEmitter();
        this.drawError = new EventEmitter();
        this.staticStyle = new Style({
            fill: new Fill({
                color: 'rgba(0,0,0,0)',
            }),
        });
        this.onDrawStart = (e) => {
            const startCoordinate = e.feature.getGeometry().getCoordinates()[0][0];
            const startPixel = this.map.instance.getPixelFromCoordinate(startCoordinate);
            const foundFeatureLike = this.map.instance.forEachFeatureAtPixel(startPixel, (feature) => {
                return feature;
            });
            if (foundFeatureLike?.getGeometry().getType() === 'Polygon') {
                this.foundFeatureToApplyEnclave = foundFeatureLike;
                this.foundPolygonToApplyEnclave = this.foundFeatureToApplyEnclave.getGeometry();
                e.feature.getGeometry().on('change', this.onGeomChange);
                this.map.instance.on('click', this.onMapClick);
            }
            else {
                this.drawError.emit({
                    type: DrawHoleInPolygonInteractionErrorType.MoPolygonFound,
                    event: e,
                    message: 'No polygon found to draw hole.',
                });
                console.warn('No polygon found to draw hole.');
                e.target.abortDrawing();
            }
        };
        this.onGeomChange = (e) => {
            const coordinates = e.target.getCoordinates()[0];
            if (coordinates.every((coord) => this.foundPolygonToApplyEnclave.intersectsCoordinate(coord))) {
                const linear_ring = new LinearRing(coordinates);
                const polygonCoordinates = this.foundPolygonToApplyEnclave.getCoordinates();
                const coordsLength = this.foundPolygonToApplyEnclave.getCoordinates().length;
                const geom = new Polygon(polygonCoordinates.slice(0, coordsLength));
                geom.appendLinearRing(linear_ring);
                this.foundFeatureToApplyEnclave.setGeometry(geom);
            }
        };
        this.onDrawEnd = () => {
            console.log('onDrawEnd');
            this.map.instance.un('click', this.onMapClick);
            this.drawEnd.emit(new Feature(this.foundFeatureToApplyEnclave.getGeometry()));
        };
        this.onMapClick = (e) => {
            console.log('onMapClick', e);
            const coordinate = this.map.instance.getCoordinateFromPixel(e.pixel);
            if (!this.foundPolygonToApplyEnclave?.intersectsCoordinate(coordinate)) {
                e.preventDefault();
                e.stopPropagation();
                console.warn('Cannot add vertex outside the polygon');
                this.drawError.emit({
                    type: DrawHoleInPolygonInteractionErrorType.DrawVertexOutsidePolygon,
                    event: e,
                    message: 'Cannot add vertex outside the polygon',
                });
                this.drawInteractionComponent.instance.removeLastPoint();
                return false;
            }
        };
        this.drawCondition = (e) => {
            const vectorLayer = this.map.instance
                .getLayers()
                .getArray()
                .find((l) => l instanceof VectorLayer);
            if (!vectorLayer) {
                this.drawError.emit({
                    type: DrawHoleInPolygonInteractionErrorType.NoVectorLayerFound,
                    event: e,
                    message: 'No vector layer found',
                });
                return false;
            }
            const foundFeatureToRemoveEnclave = vectorLayer
                .getSource()
                .getClosestFeatureToCoordinate(e.coordinate, (feature) => {
                console.log(feature.getGeometry().intersectsCoordinate(e.coordinate));
                return feature.getGeometry().getType() === 'Polygon';
            });
            console.log('foundFeatureToApplyEnclave', foundFeatureToRemoveEnclave);
            const isPlatformModifierKey = platformModifierKey(e);
            if (isPlatformModifierKey && foundFeatureToRemoveEnclave) {
                this.checkAndRemoveHole(e, foundFeatureToRemoveEnclave);
                return false;
            }
            return true;
        };
    }
    ngOnDestroy() {
        this.map.instance.un('click', this.onMapClick);
    }
    onDrawAbort(e) {
        this.map.instance.un('click', this.onMapClick);
        console.log('onDrawAbort', e);
        const coordinates = e.feature.getGeometry().getCoordinates()[0];
        console.log('coordinates', coordinates.length);
        if (coordinates.length > 2) {
            this.removeLastLinearRing();
        }
    }
    removeLastLinearRing() {
        const polygon = this.foundFeatureToApplyEnclave.getGeometry();
        let coordinates = polygon.getCoordinates();
        console.log('coordinates', coordinates);
        coordinates = coordinates.slice(0, -1); // Remove the last linear ring
        const newPolygon = new Polygon(coordinates);
        this.foundFeatureToApplyEnclave.setGeometry(newPolygon);
    }
    checkAndRemoveHole(e, foundFeatureToApplyEnclave) {
        const polygon = foundFeatureToApplyEnclave.getGeometry();
        let coordinates = polygon.getCoordinates();
        const coordinateIndex = coordinates.slice(1).findIndex((coordinate) => {
            const polygonFromLinearRing = new Polygon([coordinate], 'XY');
            return containsCoordinate(polygonFromLinearRing.getExtent(), e.coordinate);
        });
        if (coordinateIndex > -1) {
            coordinates = coordinates.filter((_, index) => index !== coordinateIndex + 1);
            const newPolygon = new Polygon(coordinates);
            foundFeatureToApplyEnclave.setGeometry(newPolygon);
            this.drawEnd.emit(foundFeatureToApplyEnclave);
            return true;
        }
        else {
            this.drawError.emit({
                type: DrawHoleInPolygonInteractionErrorType.NoLinearRingFoundToRemove,
                event: e,
                message: 'No linear ring found to remove',
            });
            console.warn('No linear ring found to remove');
            return false;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DrawHoleInPolygonInteractionComponent, deps: [{ token: MapComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: DrawHoleInPolygonInteractionComponent, isStandalone: true, selector: "aol-interaction-draw-hole-in-polygon", outputs: { drawEnd: "drawEnd", drawError: "drawError" }, viewQueries: [{ propertyName: "drawInteractionComponent", first: true, predicate: ["drawInstance"], descendants: true }], ngImport: i0, template: `
    <aol-interaction-draw
      #drawInstance
      type="Polygon"
      (drawEnd)="onDrawEnd()"
      (drawStart)="onDrawStart($event)"
      (olDrawAbort)="onDrawAbort($event)"
      [style]="staticStyle"
      [condition]="drawCondition"
    >
    </aol-interaction-draw>
  `, isInline: true, dependencies: [{ kind: "component", type: DrawInteractionComponent, selector: "aol-interaction-draw", inputs: ["clickTolerance", "features", "source", "snapTolerance", "type", "maxPoints", "minPoints", "finishCondition", "style", "geometryFunction", "geometryName", "condition", "freehandCondition", "freehand", "wrapX"], outputs: ["olChange", "olChangeActive", "olDrawAbort", "drawEnd", "drawStart", "olError", "propertyChange"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: DrawHoleInPolygonInteractionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'aol-interaction-draw-hole-in-polygon',
                    template: `
    <aol-interaction-draw
      #drawInstance
      type="Polygon"
      (drawEnd)="onDrawEnd()"
      (drawStart)="onDrawStart($event)"
      (olDrawAbort)="onDrawAbort($event)"
      [style]="staticStyle"
      [condition]="drawCondition"
    >
    </aol-interaction-draw>
  `,
                    imports: [DrawInteractionComponent]
                }]
        }], ctorParameters: () => [{ type: MapComponent }], propDecorators: { drawInteractionComponent: [{
                type: ViewChild,
                args: ['drawInstance']
            }], drawEnd: [{
                type: Output
            }], drawError: [{
                type: Output
            }] } });

/*
 * Public API Surface of ng-openlayers
 */
const COMPONENTS = [
    MapComponent,
    ViewComponent,
    GraticuleComponent,
    LayerGroupComponent,
    LayerImageComponent,
    LayerTileComponent,
    LayerVectorComponent,
    LayerVectorTileComponent,
    SourceOsmComponent,
    SourceBingmapsComponent,
    SourceClusterComponent,
    SourceUTFGridComponent,
    SourceVectorComponent,
    SourceXYZComponent,
    SourceVectorTileComponent,
    SourceTileWMSComponent,
    SourceTileWMTSComponent,
    SourceTileJSONComponent,
    SourceGeoJSONComponent,
    SourceImageStaticComponent,
    SourceImageWMSComponent,
    SourceImageArcGISRestComponent,
    SourceRasterComponent,
    FeatureComponent,
    GeometryLinestringComponent,
    GeometryMultiLinestringComponent,
    GeometryMultiPointComponent,
    GeometryMultiPolygonComponent,
    GeometryPointComponent,
    GeometryPolygonComponent,
    GeometryCircleComponent,
    CoordinateComponent,
    CollectionCoordinatesComponent,
    StyleComponent,
    StyleCircleComponent,
    StyleFillComponent,
    StyleIconComponent,
    StyleStrokeComponent,
    StyleTextComponent,
    DefaultControlComponent,
    ControlComponent,
    ControlAttributionComponent,
    ControlFullScreenComponent,
    ControlMousePositionComponent,
    ControlOverviewMapComponent,
    ControlRotateComponent,
    ControlScaleLineComponent,
    ControlZoomComponent,
    ControlZoomSliderComponent,
    ControlZoomToExtentComponent,
    FormatMVTComponent,
    TileGridComponent,
    TileGridWMTSComponent,
    DefaultInteractionComponent,
    DoubleClickZoomInteractionComponent,
    DragAndDropInteractionComponent,
    DragBoxInteractionComponent,
    DragPanInteractionComponent,
    DragRotateInteractionComponent,
    DragRotateAndZoomInteractionComponent,
    DragZoomInteractionComponent,
    MouseWheelZoomInteractionComponent,
    PinchZoomInteractionComponent,
    DrawInteractionComponent,
    SelectInteractionComponent,
    ModifyInteractionComponent,
    TranslateInteractionComponent,
    DrawHoleInPolygonInteractionComponent,
    OverlayComponent,
    ContentComponent,
    AttributionsComponent,
    AttributionComponent,
];
class AngularOpenlayersModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: AngularOpenlayersModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.0.3", ngImport: i0, type: AngularOpenlayersModule, imports: [CommonModule, MapComponent,
            ViewComponent,
            GraticuleComponent,
            LayerGroupComponent,
            LayerImageComponent,
            LayerTileComponent,
            LayerVectorComponent,
            LayerVectorTileComponent,
            SourceOsmComponent,
            SourceBingmapsComponent,
            SourceClusterComponent,
            SourceUTFGridComponent,
            SourceVectorComponent,
            SourceXYZComponent,
            SourceVectorTileComponent,
            SourceTileWMSComponent,
            SourceTileWMTSComponent,
            SourceTileJSONComponent,
            SourceGeoJSONComponent,
            SourceImageStaticComponent,
            SourceImageWMSComponent,
            SourceImageArcGISRestComponent,
            SourceRasterComponent,
            FeatureComponent,
            GeometryLinestringComponent,
            GeometryMultiLinestringComponent,
            GeometryMultiPointComponent,
            GeometryMultiPolygonComponent,
            GeometryPointComponent,
            GeometryPolygonComponent,
            GeometryCircleComponent,
            CoordinateComponent,
            CollectionCoordinatesComponent,
            StyleComponent,
            StyleCircleComponent,
            StyleFillComponent,
            StyleIconComponent,
            StyleStrokeComponent,
            StyleTextComponent,
            DefaultControlComponent,
            ControlComponent,
            ControlAttributionComponent,
            ControlFullScreenComponent,
            ControlMousePositionComponent,
            ControlOverviewMapComponent,
            ControlRotateComponent,
            ControlScaleLineComponent,
            ControlZoomComponent,
            ControlZoomSliderComponent,
            ControlZoomToExtentComponent,
            FormatMVTComponent,
            TileGridComponent,
            TileGridWMTSComponent,
            DefaultInteractionComponent,
            DoubleClickZoomInteractionComponent,
            DragAndDropInteractionComponent,
            DragBoxInteractionComponent,
            DragPanInteractionComponent,
            DragRotateInteractionComponent,
            DragRotateAndZoomInteractionComponent,
            DragZoomInteractionComponent,
            MouseWheelZoomInteractionComponent,
            PinchZoomInteractionComponent,
            DrawInteractionComponent,
            SelectInteractionComponent,
            ModifyInteractionComponent,
            TranslateInteractionComponent,
            DrawHoleInPolygonInteractionComponent,
            OverlayComponent,
            ContentComponent,
            AttributionsComponent,
            AttributionComponent], exports: [MapComponent,
            ViewComponent,
            GraticuleComponent,
            LayerGroupComponent,
            LayerImageComponent,
            LayerTileComponent,
            LayerVectorComponent,
            LayerVectorTileComponent,
            SourceOsmComponent,
            SourceBingmapsComponent,
            SourceClusterComponent,
            SourceUTFGridComponent,
            SourceVectorComponent,
            SourceXYZComponent,
            SourceVectorTileComponent,
            SourceTileWMSComponent,
            SourceTileWMTSComponent,
            SourceTileJSONComponent,
            SourceGeoJSONComponent,
            SourceImageStaticComponent,
            SourceImageWMSComponent,
            SourceImageArcGISRestComponent,
            SourceRasterComponent,
            FeatureComponent,
            GeometryLinestringComponent,
            GeometryMultiLinestringComponent,
            GeometryMultiPointComponent,
            GeometryMultiPolygonComponent,
            GeometryPointComponent,
            GeometryPolygonComponent,
            GeometryCircleComponent,
            CoordinateComponent,
            CollectionCoordinatesComponent,
            StyleComponent,
            StyleCircleComponent,
            StyleFillComponent,
            StyleIconComponent,
            StyleStrokeComponent,
            StyleTextComponent,
            DefaultControlComponent,
            ControlComponent,
            ControlAttributionComponent,
            ControlFullScreenComponent,
            ControlMousePositionComponent,
            ControlOverviewMapComponent,
            ControlRotateComponent,
            ControlScaleLineComponent,
            ControlZoomComponent,
            ControlZoomSliderComponent,
            ControlZoomToExtentComponent,
            FormatMVTComponent,
            TileGridComponent,
            TileGridWMTSComponent,
            DefaultInteractionComponent,
            DoubleClickZoomInteractionComponent,
            DragAndDropInteractionComponent,
            DragBoxInteractionComponent,
            DragPanInteractionComponent,
            DragRotateInteractionComponent,
            DragRotateAndZoomInteractionComponent,
            DragZoomInteractionComponent,
            MouseWheelZoomInteractionComponent,
            PinchZoomInteractionComponent,
            DrawInteractionComponent,
            SelectInteractionComponent,
            ModifyInteractionComponent,
            TranslateInteractionComponent,
            DrawHoleInPolygonInteractionComponent,
            OverlayComponent,
            ContentComponent,
            AttributionsComponent,
            AttributionComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: AngularOpenlayersModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: AngularOpenlayersModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, ...COMPONENTS],
                    exports: COMPONENTS,
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AngularOpenlayersModule, AttributionComponent, AttributionsComponent, CollectionCoordinatesComponent, ContentComponent, ControlAttributionComponent, ControlComponent, ControlFullScreenComponent, ControlMousePositionComponent, ControlOverviewMapComponent, ControlRotateComponent, ControlScaleLineComponent, ControlZoomComponent, ControlZoomSliderComponent, ControlZoomToExtentComponent, CoordinateComponent, DefaultControlComponent, DefaultInteractionComponent, DoubleClickZoomInteractionComponent, DragAndDropInteractionComponent, DragBoxInteractionComponent, DragPanInteractionComponent, DragRotateAndZoomInteractionComponent, DragRotateInteractionComponent, DragZoomInteractionComponent, DrawHoleInPolygonInteractionComponent, DrawHoleInPolygonInteractionErrorType, DrawInteractionComponent, FeatureComponent, FormatMVTComponent, GeometryCircleComponent, GeometryLinestringComponent, GeometryMultiLinestringComponent, GeometryMultiPointComponent, GeometryMultiPolygonComponent, GeometryPointComponent, GeometryPolygonComponent, GraticuleComponent, KeyboardPanInteractionComponent, KeyboardZoomInteractionComponent, LayerComponent, LayerGroupComponent, LayerImageComponent, LayerTileComponent, LayerVectorComponent, LayerVectorTileComponent, MapComponent, ModifyInteractionComponent, MouseWheelZoomInteractionComponent, OverlayComponent, PinchZoomInteractionComponent, SelectInteractionComponent, SimpleGeometryComponent, SourceBingmapsComponent, SourceClusterComponent, SourceComponent, SourceGeoJSONComponent, SourceImageArcGISRestComponent, SourceImageStaticComponent, SourceImageWMSComponent, SourceOsmComponent, SourceRasterComponent, SourceTileJSONComponent, SourceTileWMSComponent, SourceTileWMTSComponent, SourceUTFGridComponent, SourceVectorComponent, SourceVectorTileComponent, SourceXYZComponent, StyleCircleComponent, StyleComponent, StyleFillComponent, StyleIconComponent, StyleStrokeComponent, StyleTextComponent, TileGridComponent, TileGridWMTSComponent, TranslateInteractionComponent, ViewComponent };
